import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import BottomQuickActions from "../components/BottomQuickActions";
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  addMonths, 
  subMonths, 
  addWeeks, 
  subWeeks,
  addDays,
  subDays,
  isSameDay, 
  parseISO,
  eachDayOfInterval,
  startOfDay,
  endOfDay,
  differenceInMinutes,
  isSaturday,
  isSunday,
  isWeekend,
  addMinutes,
} from "date-fns";
import { parseISOInCompanyTimezone, nowInCompanyTimezone, toUTCISO, localTimeToUTCISO } from "../utils/calendarTimezone";
import { safeReadBody, bestErrorMessage, isOutsideHoursWarning } from "../utils/apiHelpers";
import { useFirstValueOnboarding } from "../contexts/FirstValueOnboardingContext";
import { GuidedCalendarSuccessOverlay } from "../components/FirstValueOnboardingOverlay";
import {
  CaretLeft,
  CaretRight,
  CalendarBlank,
  Clock,
  Plus,
  User,
  Briefcase,
  Play,
  CheckCircle,
  SquaresFour,
  Rows,
  Calendar as CalendarIcon,
  DotsSixVertical,
  PencilSimple,
  Trash,
} from "phosphor-react";
import { getDefaultSelectedTechnicians } from "../utils/technicianDefaults";
import AddressSelector from "../components/AddressSelector";
import ScheduleVisitModal from "../components/ScheduleVisitModal";
import SpanishTranslationCTA from "../components/SpanishTranslationCTA";
import { useEntitlementsWithFetch } from "../contexts/EntitlementContext";

// Drag & Drop imports
import {
  DndContext,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  useDroppable,
  useDraggable,
} from "@dnd-kit/core";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Constants for resize functionality
const RESIZE_GRID_MINUTES = 15;
const MIN_VISIT_DURATION_MINUTES = 15;
const SLOT_HEIGHT_PX = 60; // Height of one hour slot in pixels (for Week view)
const DAY_SLOT_HEIGHT_PX = 80; // Height of one hour slot in pixels (for Day view)
const PIXELS_PER_HOUR = SLOT_HEIGHT_PX; // Alias for backwards compatibility

// Calculate pixels per minute based on view
const getPixelsPerMinute = (viewMode) => {
  const slotHeight = viewMode === 'day' ? DAY_SLOT_HEIGHT_PX : SLOT_HEIGHT_PX;
  return slotHeight / 60;
};

// Calculate event top position and height based on times
const calculateEventPosition = (startTime, endTime, businessHoursStart, viewMode) => {
  const pixelsPerMinute = getPixelsPerMinute(viewMode);
  const bhStart = parseTime(businessHoursStart);
  const bhStartMinutes = bhStart.hours * 60 + bhStart.minutes;
  
  const eventStartMinutes = startTime.getHours() * 60 + startTime.getMinutes();
  const eventEndMinutes = endTime.getHours() * 60 + endTime.getMinutes();
  
  const topOffset = (eventStartMinutes - bhStartMinutes) * pixelsPerMinute;
  const height = (eventEndMinutes - eventStartMinutes) * pixelsPerMinute;
  
  return { top: topOffset, height: Math.max(height, 20) }; // Minimum 20px height
};

// ========================
// OVERLAP DETECTION & LAYOUT
// ========================
// Detects overlapping events and calculates horizontal positioning (width, left offset)
// for side-by-side rendering in Day and Week views.

/**
 * Checks if two time ranges overlap
 */
const timeRangesOverlap = (start1, end1, start2, end2) => {
  return start1 < end2 && start2 < end1;
};

/**
 * Groups events into overlap clusters and assigns horizontal layout positions.
 * Returns a Map of eventId -> { columnIndex, totalColumns }
 */
const calculateOverlapLayout = (events) => {
  if (!events || events.length === 0) return new Map();
  
  // Parse and sort events by start time
  const parsedEvents = events
    .filter(e => e.start && e.end)
    .map(e => ({
      ...e,
      startTime: parseISO(e.start).getTime(),
      endTime: parseISO(e.end).getTime(),
    }))
    .sort((a, b) => a.startTime - b.startTime || a.endTime - b.endTime);
  
  if (parsedEvents.length === 0) return new Map();
  
  // Find overlap groups using a graph-based approach
  const overlapGroups = [];
  const visited = new Set();
  
  for (let i = 0; i < parsedEvents.length; i++) {
    if (visited.has(parsedEvents[i].id)) continue;
    
    // Start a new group
    const group = [parsedEvents[i]];
    visited.add(parsedEvents[i].id);
    
    // Find all events that overlap with any event in this group
    let expanded = true;
    while (expanded) {
      expanded = false;
      for (let j = 0; j < parsedEvents.length; j++) {
        if (visited.has(parsedEvents[j].id)) continue;
        
        // Check if this event overlaps with any event in the current group
        const overlapsWithGroup = group.some(groupEvent => 
          timeRangesOverlap(
            groupEvent.startTime, 
            groupEvent.endTime,
            parsedEvents[j].startTime,
            parsedEvents[j].endTime
          )
        );
        
        if (overlapsWithGroup) {
          group.push(parsedEvents[j]);
          visited.add(parsedEvents[j].id);
          expanded = true;
        }
      }
    }
    
    overlapGroups.push(group);
  }
  
  // Assign columns within each group
  const layoutMap = new Map();
  
  for (const group of overlapGroups) {
    if (group.length === 1) {
      // No overlaps - full width
      layoutMap.set(group[0].id, { columnIndex: 0, totalColumns: 1 });
      continue;
    }
    
    // Sort group by start time for column assignment
    group.sort((a, b) => a.startTime - b.startTime || a.endTime - b.endTime);
    
    // Greedy column assignment
    const columns = []; // Array of { endTime, eventId }[]
    
    for (const event of group) {
      // Find first column where this event fits (no overlap)
      let placed = false;
      for (let col = 0; col < columns.length; col++) {
        const lastInColumn = columns[col][columns[col].length - 1];
        if (event.startTime >= lastInColumn.endTime) {
          columns[col].push({ endTime: event.endTime, eventId: event.id });
          layoutMap.set(event.id, { columnIndex: col, totalColumns: group.length });
          placed = true;
          break;
        }
      }
      
      if (!placed) {
        // Create new column
        columns.push([{ endTime: event.endTime, eventId: event.id }]);
        layoutMap.set(event.id, { columnIndex: columns.length - 1, totalColumns: group.length });
      }
    }
    
    // Update totalColumns to actual number of columns used
    const actualColumns = columns.length;
    for (const event of group) {
      const layout = layoutMap.get(event.id);
      if (layout) {
        layout.totalColumns = actualColumns;
      }
    }
  }
  
  return layoutMap;
};

/**
 * Calculate CSS styles for overlapping event positioning
 * @param {number} columnIndex - Which column (0-indexed)
 * @param {number} totalColumns - Total columns in overlap group
 * @param {number} gutterPx - Gutter between events (default 3px)
 */
const getOverlapStyles = (columnIndex, totalColumns, gutterPx = 3) => {
  if (totalColumns <= 1) {
    return { left: '2px', right: '2px', width: 'auto' };
  }
  
  const totalGutter = gutterPx * (totalColumns - 1);
  const widthPercent = (100 - (totalGutter / totalColumns * 100 / 100 * totalColumns)) / totalColumns;
  const width = `calc(${100 / totalColumns}% - ${gutterPx}px)`;
  const left = `calc(${(100 / totalColumns) * columnIndex}% + ${columnIndex > 0 ? gutterPx / 2 : 0}px)`;
  
  return {
    left,
    width,
    right: 'auto',
  };
};

// Helper to get/set calendar view preference in localStorage (per-user)
const getStoredCalendarView = (userId) => {
  try {
    const key = `calendar:view:${userId || 'default'}`;
    const stored = localStorage.getItem(key);
    if (stored && ["day", "week", "month"].includes(stored)) {
      return stored;
    }
  } catch (e) {
    console.warn("Could not read calendar view preference:", e);
  }
  return "week"; // Default to week view
};

const setStoredCalendarView = (userId, view) => {
  try {
    const key = `calendar:view:${userId || 'default'}`;
    localStorage.setItem(key, view);
  } catch (e) {
    console.warn("Could not save calendar view preference:", e);
  }
};

/**
 * Parse time string "HH:MM" into { hours, minutes }
 */
const parseTime = (timeStr) => {
  if (!timeStr) return { hours: 7, minutes: 0 };
  const [h, m] = timeStr.split(":").map(Number);
  return { hours: isNaN(h) ? 7 : h, minutes: isNaN(m) ? 0 : m };
};

/**
 * Generate hour slots based on business hours
 * Returns array of { hour, minute, label } for each slot
 */
const generateTimeSlots = (startTime, endTime, intervalMinutes = 60) => {
  const start = parseTime(startTime);
  const end = parseTime(endTime);
  
  const slots = [];
  let currentHour = start.hours;
  let currentMinute = start.minutes;
  
  const endMinutes = end.hours * 60 + end.minutes;
  
  while (currentHour * 60 + currentMinute < endMinutes) {
    slots.push({
      hour: currentHour,
      minute: currentMinute,
      label: formatTimeLabel(currentHour, currentMinute)
    });
    
    currentMinute += intervalMinutes;
    while (currentMinute >= 60) {
      currentMinute -= 60;
      currentHour += 1;
    }
  }
  
  return slots;
};

/**
 * Format time label without depending on current time
 */
const formatTimeLabel = (hour, minute = 0) => {
  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;
  const displayMinute = minute.toString().padStart(2, '0');
  return `${displayHour}:${displayMinute} ${period}`;
};

/**
 * Check if a multi-day schedule would include any weekend days
 */
const scheduleIncludesWeekend = (startDate, days, skipWeekends = false) => {
  if (days <= 1) return false;
  
  let currentDate = new Date(startDate);
  let daysChecked = 0;
  
  while (daysChecked < days) {
    if (skipWeekends) {
      // Skip weekend days when counting
      if (isWeekend(currentDate)) {
        currentDate = addDays(currentDate, 1);
        continue;
      }
    } else {
      // Check if this day is a weekend
      if (isWeekend(currentDate)) {
        return true;
      }
    }
    daysChecked++;
    currentDate = addDays(currentDate, 1);
  }
  
  return false;
};

/**
 * Get the next weekday from a given date
 */
const getNextWeekday = (date) => {
  let result = new Date(date);
  while (isWeekend(result)) {
    result = addDays(result, 1);
  }
  return result;
};

/**
 * Generate preview dates for multi-day visits
 */
const generateVisitDates = (startDate, days, skipWeekends = false) => {
  const dates = [];
  let currentDate = new Date(startDate);
  
  // If skip weekends and starting on weekend, move to Monday
  if (skipWeekends && isWeekend(currentDate)) {
    currentDate = getNextWeekday(currentDate);
  }
  
  let count = 0;
  while (count < days) {
    if (skipWeekends && isWeekend(currentDate)) {
      currentDate = addDays(currentDate, 1);
      continue;
    }
    dates.push(new Date(currentDate));
    count++;
    currentDate = addDays(currentDate, 1);
  }
  
  return dates;
};


// ========================
// OFF-HOURS EVENT CATEGORIZATION
// ========================

/**
 * Categorize events by their relationship to business hours.
 * Returns grid events (clamped to business hours) and off-hours items.
 * 
 * @param {Array} events - Events for a specific day
 * @param {string} openTime - Business hours start (e.g., "07:00")
 * @param {string} closeTime - Business hours end (e.g., "19:00")
 * @param {string} companyTimezone - Timezone for parsing
 * @returns {{ gridEvents: Array, beforeOpenItems: Array, afterCloseItems: Array }}
 */
const categorizeEventsByBusinessHours = (events, openTime, closeTime, companyTimezone) => {
  const gridEvents = [];
  const beforeOpenItems = [];
  const afterCloseItems = [];
  
  const open = parseTime(openTime);
  const close = parseTime(closeTime);
  const openMinutes = open.hours * 60 + open.minutes;
  const closeMinutes = close.hours * 60 + close.minutes;
  
  for (const event of events) {
    if (!event.start) continue;
    
    try {
      const eventStart = parseISOInCompanyTimezone(event.start, companyTimezone);
      const eventEnd = event.end 
        ? parseISOInCompanyTimezone(event.end, companyTimezone)
        : addMinutes(eventStart, 60); // Default 1 hour if no end
      
      const startMinutes = eventStart.getHours() * 60 + eventStart.getMinutes();
      
      // Handle events that span midnight (end date > start date)
      // If end is on a different day, treat endMinutes as end-of-day (e.g., 23:59)
      const sameDay = isSameDay(eventStart, eventEnd);
      const endMinutes = sameDay 
        ? (eventEnd.getHours() * 60 + eventEnd.getMinutes())
        : (23 * 60 + 59); // Treat as ending at end of day for classification
      
      // Full time range for display (consistent with Visit Details modal)
      const fullDisplayRange = `${format(eventStart, "h:mm a")} - ${format(eventEnd, "h:mm a")}`;
      
      // Case 1: Fully in-hours
      if (startMinutes >= openMinutes && endMinutes <= closeMinutes) {
        gridEvents.push(event);
        continue;
      }
      
      // Case 2: Fully before open
      if (endMinutes <= openMinutes) {
        beforeOpenItems.push({
          ...event,
          trueStart: eventStart,
          trueEnd: eventEnd,
          displayRange: fullDisplayRange,
          fullDisplayRange: fullDisplayRange,
          outsideSegment: false, // Entire visit is outside hours
        });
        continue;
      }
      
      // Case 3: Fully after close
      if (startMinutes >= closeMinutes) {
        afterCloseItems.push({
          ...event,
          trueStart: eventStart,
          trueEnd: eventEnd,
          displayRange: fullDisplayRange,
          fullDisplayRange: fullDisplayRange,
          outsideSegment: false, // Entire visit is outside hours
        });
        continue;
      }
      
      // Case 4: Spans into in-hours from before open
      if (startMinutes < openMinutes && endMinutes > openMinutes && endMinutes <= closeMinutes) {
        // Clamp the grid event to start at open time
        const clampedStart = new Date(eventStart);
        clampedStart.setHours(open.hours, open.minutes, 0, 0);
        
        // CRITICAL: Convert to UTC using company timezone
        const clampedStartISO = toUTCISO(clampedStart, companyTimezone);
        
        gridEvents.push({
          ...event,
          clampedStart: clampedStartISO,
          originalStart: event.start,
        });
        
        // Add to before-open strip with FULL time for clarity
        beforeOpenItems.push({
          ...event,
          trueStart: eventStart,
          trueEnd: eventEnd,
          displayRange: fullDisplayRange,
          fullDisplayRange: fullDisplayRange,
          outsideSegment: true, // Part of visit is outside hours
        });
        continue;
      }
      
      // Case 5: Spans out of in-hours after close
      if (startMinutes < closeMinutes && startMinutes >= openMinutes && endMinutes > closeMinutes) {
        // Clamp the grid event to end at close time
        // Create a new date with the same year/month/day as event start, but at close time
        const clampedEnd = new Date(eventStart);
        clampedEnd.setHours(close.hours, close.minutes, 0, 0);
        
        // CRITICAL: Convert back to UTC using the company timezone
        // This ensures the ISO string represents the correct moment in time
        const clampedEndISO = toUTCISO(clampedEnd, companyTimezone);
        
        gridEvents.push({
          ...event,
          clampedEnd: clampedEndISO,
          originalEnd: event.end,
        });
        
        // Add to after-close strip with FULL time for clarity
        afterCloseItems.push({
          ...event,
          trueStart: eventStart,
          trueEnd: eventEnd,
          displayRange: fullDisplayRange,
          fullDisplayRange: fullDisplayRange,
          outsideSegment: true, // Part of visit is outside hours
        });
        continue;
      }
      
      // Case 6: Spans both sides (start before open AND end after close)
      if (startMinutes < openMinutes && endMinutes > closeMinutes) {
        // Clamp grid event to full business hours
        const clampedStart = new Date(eventStart);
        clampedStart.setHours(open.hours, open.minutes, 0, 0);
        const clampedEnd = new Date(eventStart);
        clampedEnd.setHours(close.hours, close.minutes, 0, 0);
        
        // CRITICAL: Convert both to UTC using company timezone
        const clampedStartISO = toUTCISO(clampedStart, companyTimezone);
        const clampedEndISO = toUTCISO(clampedEnd, companyTimezone);
        
        gridEvents.push({
          ...event,
          clampedStart: clampedStartISO,
          clampedEnd: clampedEndISO,
          originalStart: event.start,
          originalEnd: event.end,
        });
        
        // Add to before-open strip with FULL time
        beforeOpenItems.push({
          ...event,
          trueStart: eventStart,
          trueEnd: eventEnd,
          displayRange: fullDisplayRange,
          fullDisplayRange: fullDisplayRange,
          outsideSegment: true,
        });
        
        // Add to after-close strip with FULL time
        afterCloseItems.push({
          ...event,
          trueStart: eventStart,
          trueEnd: eventEnd,
          displayRange: fullDisplayRange,
          fullDisplayRange: fullDisplayRange,
          outsideSegment: true,
        });
        continue;
      }
      
      // Edge case: starts before open but ends before close (different from case 4)
      if (startMinutes < openMinutes && endMinutes > openMinutes) {
        const clampedStart = new Date(eventStart);
        clampedStart.setHours(open.hours, open.minutes, 0, 0);
        
        // If ends after close, clamp end to close time
        let clampedEnd;
        if (endMinutes > closeMinutes) {
          clampedEnd = new Date(eventStart);
          clampedEnd.setHours(close.hours, close.minutes, 0, 0);
        } else {
          clampedEnd = eventEnd;
        }
        
        // CRITICAL: Convert to UTC using company timezone
        const clampedStartISO = toUTCISO(clampedStart, companyTimezone);
        const clampedEndISO = endMinutes > closeMinutes ? toUTCISO(clampedEnd, companyTimezone) : event.end;
        
        gridEvents.push({
          ...event,
          clampedStart: clampedStartISO,
          clampedEnd: clampedEndISO,
          originalStart: event.start,
          originalEnd: event.end,
        });
        
        beforeOpenItems.push({
          ...event,
          trueStart: eventStart,
          trueEnd: eventEnd,
          displayRange: fullDisplayRange,
          fullDisplayRange: fullDisplayRange,
          outsideSegment: true,
        });
      }
      
    } catch (e) {
      // If parsing fails, include in grid as-is
      gridEvents.push(event);
    }
  }
  
  return { gridEvents, beforeOpenItems, afterCloseItems };
};

/**
 * Collapsible off-hours strip component
 * Shows visits that have time portions outside business hours.
 * Displays the FULL visit time range for clarity (same as Visit Details modal).
 */
function OffHoursStrip({ 
  items, 
  label, 
  type, // "before" or "after"
  onItemClick,
  dayKey,
}) {
  const [expanded, setExpanded] = useState(false);
  
  if (!items || items.length === 0) return null;
  
  const bgColor = type === "before" ? "bg-purple-50" : "bg-indigo-50";
  const borderColor = type === "before" ? "border-purple-200" : "border-indigo-200";
  const textColor = type === "before" ? "text-purple-700" : "text-indigo-700";
  const badgeBg = type === "before" ? "bg-purple-100" : "bg-indigo-100";
  
  return (
    <div className={`${bgColor} ${borderColor} border rounded-sm overflow-hidden`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full px-2 py-1 flex items-center justify-between text-xs ${textColor} hover:bg-opacity-80`}
        data-testid={`off-hours-strip-${type}-${dayKey}`}
      >
        <span className="font-medium flex items-center gap-1">
          <Clock size={12} />
          {label} ({items.length})
        </span>
        <span className="text-[10px]">{expanded ? "▲" : "▼"}</span>
      </button>
      
      {expanded && (
        <div className="px-2 pb-2 space-y-1">
          {items.map((item, idx) => (
            <div
              key={`${item.id}-${idx}`}
              onClick={() => onItemClick(item)}
              className={`${badgeBg} rounded px-2 py-1 cursor-pointer hover:opacity-80`}
              data-testid={`off-hours-item-${item.id}`}
            >
              <div className={`text-[10px] font-medium ${textColor} truncate`}>
                {item.title}
              </div>
              <div className="text-[9px] text-zinc-600 truncate">
                {item.customer}
              </div>
              <div className="text-[9px] text-zinc-500">
                {/* Show FULL visit time for consistency with Visit Details modal */}
                {item.fullDisplayRange || item.displayRange}
                {item.outsideSegment && (
                  <span className="ml-1 opacity-70 italic">
                    ({type === "before" ? "starts early" : "runs late"})
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ========================
// DRAGGABLE EVENT COMPONENT
// ========================
function DraggableEvent({ event, children, viewMode, disabled }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: event.id,
    data: { event, viewMode },
    disabled: disabled,
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    zIndex: isDragging ? 1000 : 1,
    opacity: isDragging ? 0.8 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
  } : {
    cursor: disabled ? 'default' : 'grab',
  };

  return (
    <div ref={setNodeRef} style={style} className="h-full" {...(disabled ? {} : { ...listeners, ...attributes })}>
      {children}
    </div>
  );
}

// ========================
// RESIZABLE EVENT COMPONENT  
// ========================
function ResizableEvent({ 
  event, 
  children, 
  viewMode, 
  businessHours,
  onResize,
  onClick,
  canResize = true,
  renderWithHeight = false, // If true, renders with calculated height
  overlapLayout = null, // { columnIndex, totalColumns } for overlap positioning
}) {
  const [isResizing, setIsResizing] = useState(false);
  const [resizeEdge, setResizeEdge] = useState(null); // 'top' or 'bottom'
  const [resizeTooltip, setResizeTooltip] = useState(null);
  const [provisionalTimes, setProvisionalTimes] = useState(null); // For live preview
  const [isHovered, setIsHovered] = useState(false); // For z-index on hover
  const containerRef = useRef(null);
  
  // Store refs for resize state to avoid closure issues
  const resizeStateRef = useRef({
    startY: 0,
    originalStart: null,
    originalEnd: null,
    currentStart: null,
    currentEnd: null,
    edge: null,
    businessStart: null,
    businessEnd: null,
    isActive: false
  });

  // Parse event times in company timezone
  const companyTimezone = businessHours?.timezone;
  const eventStart = event.start ? parseISOInCompanyTimezone(event.start, companyTimezone) : null;
  const eventEnd = event.end ? parseISOInCompanyTimezone(event.end, companyTimezone) : null;

  // Check if event can be resized
  const isScheduled = event.status === "scheduled";
  const canBeResized = canResize && isScheduled && eventStart && eventEnd;

  // Determine if provisional times should be cleared (event was updated to match)
  const shouldClearProvisional = useMemo(() => {
    if (!provisionalTimes || !eventStart || !eventEnd) return false;
    const startMatches = Math.abs(eventStart.getTime() - provisionalTimes.start.getTime()) < 60000;
    const endMatches = Math.abs(eventEnd.getTime() - provisionalTimes.end.getTime()) < 60000;
    return startMatches && endMatches;
  }, [eventStart, eventEnd, provisionalTimes]);

  // Use derived state for display - clear provisional when event matches
  const effectiveProvisionalTimes = shouldClearProvisional ? null : provisionalTimes;
  
  // Calculate display times (provisional during resize, or actual)
  const displayStart = effectiveProvisionalTimes?.start || eventStart;
  const displayEnd = effectiveProvisionalTimes?.end || eventEnd;

  // Calculate position/height if renderWithHeight is true
  const positionStyle = useMemo(() => {
    if (!renderWithHeight || !displayStart || !displayEnd) return {};
    
    const { top, height } = calculateEventPosition(
      displayStart, 
      displayEnd, 
      businessHours.start, 
      viewMode
    );
    
    // Debug logging
    if (process.env.NODE_ENV === 'development') {
      const durationMins = differenceInMinutes(displayEnd, displayStart);
      const ppm = getPixelsPerMinute(viewMode);
      console.log(`[EventLayout] ${event.id} (${viewMode}): start=${displayStart.toISOString()}, end=${displayEnd.toISOString()}, duration=${durationMins}min, ppm=${ppm}, height=${height}px, top=${top}px`);
    }
    
    // Get overlap positioning styles
    const overlapStyles = overlapLayout 
      ? getOverlapStyles(overlapLayout.columnIndex, overlapLayout.totalColumns)
      : { left: '2px', right: '2px', width: 'auto' };
    
    return {
      position: 'absolute',
      top: `${top}px`,
      height: `${height}px`,
      ...overlapStyles,
      zIndex: isResizing ? 50 : (isHovered ? 10 : 1),
    };
  }, [renderWithHeight, displayStart, displayEnd, businessHours.start, viewMode, isResizing, isHovered, event.id, overlapLayout]);

  // Handle resize start
  const handleResizeStart = useCallback((e, edge) => {
    if (!canBeResized || !eventStart || !eventEnd) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    // Parse business hours
    const businessStart = parseTime(businessHours.start);
    const businessEnd = parseTime(businessHours.end);
    
    // Store state in ref - mark as active
    resizeStateRef.current = {
      startY: e.clientY,
      originalStart: new Date(eventStart),
      originalEnd: new Date(eventEnd),
      currentStart: new Date(eventStart),
      currentEnd: new Date(eventEnd),
      edge: edge,
      businessStart: businessStart,
      businessEnd: businessEnd,
      isActive: true
    };
    
    setIsResizing(true);
    setResizeEdge(edge);
    setProvisionalTimes({ start: new Date(eventStart), end: new Date(eventEnd) });
    
    // Log for debugging
    console.log('[Resize] Start:', {
      visitId: event.id,
      edge,
      originalStart: eventStart.toISOString(),
      originalEnd: eventEnd.toISOString()
    });
    
    // Add global event listeners
    const handleMove = (moveEvent) => {
      const state = resizeStateRef.current;
      if (!state.isActive || !state.originalStart) return;

      const deltaY = moveEvent.clientY - state.startY;
      const pixelsPerMinute = getPixelsPerMinute(viewMode);
      const deltaMinutes = Math.round((deltaY / pixelsPerMinute) / RESIZE_GRID_MINUTES) * RESIZE_GRID_MINUTES;

      let newStart = new Date(state.originalStart);
      let newEnd = new Date(state.originalEnd);

      // Snap time to 15-minute grid
      const snapToGrid = (date) => {
        const minutes = date.getMinutes();
        const snappedMinutes = Math.round(minutes / RESIZE_GRID_MINUTES) * RESIZE_GRID_MINUTES;
        const newDate = new Date(date);
        newDate.setMinutes(snappedMinutes);
        newDate.setSeconds(0);
        newDate.setMilliseconds(0);
        return newDate;
      };

      // Clamp time within business hours
      const clampToBusinessHours = (date) => {
        const dayStart = new Date(date);
        dayStart.setHours(state.businessStart.hours, state.businessStart.minutes, 0, 0);
        
        const dayEnd = new Date(date);
        dayEnd.setHours(state.businessEnd.hours, state.businessEnd.minutes, 0, 0);
        
        if (date < dayStart) return dayStart;
        if (date > dayEnd) return dayEnd;
        return date;
      };

      if (state.edge === 'top') {
        // Adjusting start time
        newStart = addMinutes(state.originalStart, deltaMinutes);
        newStart = snapToGrid(newStart);
        newStart = clampToBusinessHours(newStart);
        
        // Ensure minimum duration
        const duration = differenceInMinutes(newEnd, newStart);
        if (duration < MIN_VISIT_DURATION_MINUTES) {
          newStart = addMinutes(newEnd, -MIN_VISIT_DURATION_MINUTES);
        }
      } else if (state.edge === 'bottom') {
        // Adjusting end time
        newEnd = addMinutes(state.originalEnd, deltaMinutes);
        newEnd = snapToGrid(newEnd);
        newEnd = clampToBusinessHours(newEnd);
        
        // Ensure minimum duration
        const duration = differenceInMinutes(newEnd, newStart);
        if (duration < MIN_VISIT_DURATION_MINUTES) {
          newEnd = addMinutes(newStart, MIN_VISIT_DURATION_MINUTES);
        }
      }

      // Update current values in ref
      state.currentStart = newStart;
      state.currentEnd = newEnd;
      
      // Update provisional times for visual preview
      setProvisionalTimes({ start: newStart, end: newEnd });
      
      // Update tooltip
      const tooltipText = state.edge === 'top' 
        ? `Starts ${format(newStart, 'h:mm a')}`
        : `Ends ${format(newEnd, 'h:mm a')}`;
      setResizeTooltip(tooltipText);
    };

    const handleEnd = () => {
      // Remove listeners immediately
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      
      // Capture final state before cleanup
      const state = resizeStateRef.current;
      if (!state.isActive) return;
      
      const finalStart = state.currentStart;
      const finalEnd = state.currentEnd;
      const originalStart = state.originalStart;
      const originalEnd = state.originalEnd;
      
      // Mark as inactive to prevent double triggers
      state.isActive = false;
      
      // Check if times actually changed
      const startChanged = finalStart && originalStart && finalStart.getTime() !== originalStart.getTime();
      const endChanged = finalEnd && originalEnd && finalEnd.getTime() !== originalEnd.getTime();
      
      console.log('[Resize] End:', {
        visitId: event.id,
        startChanged,
        endChanged,
        finalStart: finalStart?.toISOString(),
        finalEnd: finalEnd?.toISOString()
      });
      
      // Cleanup UI state
      setIsResizing(false);
      setResizeEdge(null);
      setResizeTooltip(null);
      
      // Keep provisional times until save completes (for visual continuity)
      // They will be cleared when the event prop updates with new times
      
      // Call onResize callback AFTER state cleanup
      if ((startChanged || endChanged) && onResize && finalStart && finalEnd) {
        // Use setTimeout to ensure this runs after React state updates
        setTimeout(() => {
          onResize(event.id, finalStart, finalEnd);
        }, 0);
      } else {
        // No change - clear provisional times
        setProvisionalTimes(null);
      }
      
      // Clear ref state
      resizeStateRef.current = {
        startY: 0,
        originalStart: null,
        originalEnd: null,
        currentStart: null,
        currentEnd: null,
        edge: null,
        businessStart: null,
        businessEnd: null,
        isActive: false
      };
    };
    
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);
  }, [canBeResized, eventStart, eventEnd, businessHours, onResize, event.id, viewMode]);

  return (
    <div 
      ref={containerRef}
      className={`relative group ${isResizing ? 'z-50' : ''} transition-shadow`}
      style={positionStyle}
      data-testid={`resizable-event-${event.id}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Top resize handle (start time) */}
      {canBeResized && (
        <div
          className={`absolute top-0 left-0 right-0 h-3 cursor-ns-resize z-20 
            ${isResizing && resizeEdge === 'top' ? 'bg-[#FF6600]/50' : 'bg-transparent hover:bg-[#FF6600]/30'}
            transition-colors`}
          onMouseDown={(e) => handleResizeStart(e, 'top')}
          data-testid={`resize-top-${event.id}`}
        >
          <div className="absolute top-0.5 left-1/2 -translate-x-1/2 w-8 h-1 bg-white/50 rounded-full 
            opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      )}

      {/* Event content with click handler - pass display times for rendering */}
      <div onClick={onClick} className="h-full">
        {typeof children === 'function' 
          ? children({ displayStart, displayEnd, isResizing })
          : children
        }
      </div>

      {/* Bottom resize handle (end time) */}
      {canBeResized && (
        <div
          className={`absolute bottom-0 left-0 right-0 h-3 cursor-ns-resize z-20
            ${isResizing && resizeEdge === 'bottom' ? 'bg-[#FF6600]/50' : 'bg-transparent hover:bg-[#FF6600]/30'}
            transition-colors`}
          onMouseDown={(e) => handleResizeStart(e, 'bottom')}
          data-testid={`resize-bottom-${event.id}`}
        >
          <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-8 h-1 bg-white/50 rounded-full
            opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      )}

      {/* Resize tooltip */}
      {isResizing && resizeTooltip && (
        <div 
          className={`absolute ${resizeEdge === 'top' ? '-top-8' : '-bottom-8'} left-1/2 -translate-x-1/2 
            bg-zinc-900 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap z-50`}
          data-testid={`resize-tooltip-${event.id}`}
        >
          {resizeTooltip}
        </div>
      )}

      {/* Preview overlay during resize */}
      {isResizing && (
        <div className="absolute inset-0 border-2 border-[#FF6600] border-dashed rounded pointer-events-none" />
      )}
    </div>
  );
}

// ========================
// DROPPABLE TIME SLOT COMPONENT
// ========================
function DroppableSlot({ id, children, className, onClick, style }) {
  const { isOver, setNodeRef } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      onClick={onClick}
      className={`${className} ${isOver ? 'bg-[#FF6600]/20 ring-2 ring-[#FF6600]' : ''}`}
      style={style}
    >
      {children}
    </div>
  );
}


export default function Calendar() {
  // Get userId from localStorage for per-user view preference
  const [userId, setUserId] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState(null); // null until loaded from localStorage
  const [viewInitialized, setViewInitialized] = useState(false); // Blocks fetch until view is finalized
  const [events, setEvents] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // First-value onboarding context
  const { isGuided, step: fvoStep } = useFirstValueOnboarding();
  const showFVOSuccessOverlay = isGuided && fvoStep === 'calendar_scheduled';

  // First-value onboarding: brief pulse highlight on a freshly created real
  // visit when arriving via ?highlight_visit=<id>. Clears the param + class
  // after ~3.5s. Read-only effect; does not touch any other behavior.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const highlightVisitId = searchParams.get('highlight_visit');
    if (!highlightVisitId) return;

    let cleared = false;
    const tryHighlight = (attempt = 0) => {
      const selectors = [
        `[data-testid="resizable-event-${highlightVisitId}"]`,
        `[data-testid="event-${highlightVisitId}"]`,
        `[data-testid="week-event-${highlightVisitId}"]`,
        `[data-testid="day-event-${highlightVisitId}"]`,
      ];
      let el = null;
      for (const sel of selectors) {
        el = document.querySelector(sel);
        if (el) break;
      }
      if (el) {
        el.classList.add('fvo-pulse-highlight');
        try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (_) {}
        setTimeout(() => { el.classList.remove('fvo-pulse-highlight'); }, 3500);
        if (!cleared) {
          cleared = true;
          // Strip the query param so refresh doesn't re-pulse
          const next = new URLSearchParams(searchParams);
          next.delete('highlight_visit');
          setSearchParams(next, { replace: true });
        }
      } else if (attempt < 8) {
        setTimeout(() => tryHighlight(attempt + 1), 400);
      }
    };
    tryHighlight();
  }, [searchParams, setSearchParams]);
  
  // Request sequence counter - prevents stale responses from overwriting fresh data
  const fetchSeqRef = useRef(0);
  
  // Business hours state
  const [businessHours, setBusinessHours] = useState({
    start: "07:00",
    end: "19:00",
    timezone: "America/New_York"
  });
  
  // Mini calendar popover state
  const [miniCalendarOpen, setMiniCalendarOpen] = useState(false);
  
  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null); // For opening modal from empty slot click
  
  // Visit edit/delete state
  const [isEditingVisit, setIsEditingVisit] = useState(false);
  const [editVisitForm, setEditVisitForm] = useState({
    date: new Date(),
    startTime: "09:00",
    endTime: "10:00",
    assigned_technicians: [],
    customer_address_id: null,
  });
  const [visitAddresses, setVisitAddresses] = useState([]);
  const [showDeleteVisitConfirm, setShowDeleteVisitConfirm] = useState(false);
  const [showOutsideHoursWarning, setShowOutsideHoursWarning] = useState(false);
  
  // Drag & drop state
  const [activeId, setActiveId] = useState(null);
  const [draggedEvent, setDraggedEvent] = useState(null);
  
  // Store all technicians (including inactive) for displaying historical assignments
  const [allTechnicians, setAllTechnicians] = useState([]);

  // Spanish Translation CTA state
  const [spanishCTAOpen, setSpanishCTAOpen] = useState(false);
  const [spanishTechIds, setSpanishTechIds] = useState([]); // IDs of Spanish techs in current selection
  const [pendingSaveCallback, setPendingSaveCallback] = useState(null); // Callback to execute on dismiss
  const [isSavingVisit, setIsSavingVisit] = useState(false); // Loading state for save operations
  const [visitWatchdogFired, setVisitWatchdogFired] = useState(false); // Inline watchdog warning state
  
  // In-flight operation tracking for save integrity (prevents double-submit)
  const visitSaveOpRef = useRef(0); // Increments on each save attempt
  const visitSaveAbortRef = useRef(null); // AbortController for canceling in-flight requests
  
  // Debug flag - set to true for verbose logging during development
  const DEBUG_VISIT_SAVE = false; // Gate debug logs - set true for development only
  
  // Use entitlements for Spanish Translation check
  const { hasSpanishTranslation } = useEntitlementsWithFetch();

  // ========================
  // Mobile Detection - disable drag/drop/resize on mobile
  // Uses pointer: coarse media query which covers touch devices
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  
  useEffect(() => {
    // Check for coarse pointer (touch devices) 
    const checkMobile = () => {
      const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
      const isSmallScreen = window.innerWidth < 768;
      setIsMobileDevice(hasCoarsePointer || isSmallScreen);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    // Also listen for pointer capability changes (e.g., tablet mode)
    const mediaQuery = window.matchMedia('(pointer: coarse)');
    const handlePointerChange = (e) => setIsMobileDevice(e.matches || window.innerWidth < 768);
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handlePointerChange);
    }
    
    return () => {
      window.removeEventListener('resize', checkMobile);
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handlePointerChange);
      }
    };
  }, []);

  // ========================
  // Drag sensors - require 8px movement before drag starts (prevents accidental drags)
  // Disabled on mobile
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: isMobileDevice ? Infinity : 8, // Infinity effectively disables on mobile
      },
    })
  );

  // Generate time slots based on business hours
  // Use RESIZE_GRID_MINUTES (15) for consistent drag/move snapping with resize behavior
  const timeSlots = generateTimeSlots(businessHours.start, businessHours.end, RESIZE_GRID_MINUTES);

  // Load userId and stored view preference on mount - MUST complete before first fetch
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    let uid = null;
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        uid = user.user_id || user.id || user.email || 'default';
      } catch {
        uid = 'default';
      }
    } else {
      uid = 'default';
    }
    setUserId(uid);
    
    const storedView = getStoredCalendarView(uid);
    setViewMode(storedView);
    setViewInitialized(true); // Mark initialization complete - enables fetch
  }, []);

  const handleViewModeChange = (newView) => {
    setViewMode(newView);
    setStoredCalendarView(userId, newView);
  };

  useEffect(() => {
    fetchBusinessHours();
  }, []);

  const fetchBusinessHours = async () => {
    try {
      const response = await fetch(`${API}/calendar/business-hours`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setBusinessHours({
          start: data.business_hours_start || "07:00",
          end: data.business_hours_end || "19:00",
          timezone: data.timezone || "America/New_York"
        });
        console.log('[BusinessHours] Loaded:', data);
      }
    } catch (error) {
      console.error("Error fetching business hours:", error);
    }
  };

  // Safe reload helper - NEVER throws, logs failures instead
  // This ensures secondary reload failures don't block or freeze the UI
  const safeReload = async (label, fn) => {
    try {
      await fn();
    } catch (err) {
      console.error('[SAFE_RELOAD_FAILED]', label, err?.message || err);
    }
  };

  // Individual reload functions for isolation
  const reloadEvents = useCallback(async () => {
    // Block fetch if view not yet initialized from localStorage
    if (!viewInitialized || !viewMode) {
      console.log('[reloadEvents] Skipped - view not initialized yet');
      return;
    }
    
    // Increment sequence and capture for this request
    fetchSeqRef.current += 1;
    const thisSeq = fetchSeqRef.current;
    
    let start, end;
    if (viewMode === "month") {
      start = startOfMonth(currentDate);
      end = endOfMonth(currentDate);
    } else if (viewMode === "week") {
      start = startOfWeek(currentDate);
      end = endOfWeek(currentDate);
    } else {
      start = startOfDay(currentDate);
      end = endOfDay(currentDate);
    }
    
    console.log('[reloadEvents] Fetching', { seq: thisSeq, viewMode, start: start.toISOString(), end: end.toISOString() });
    
    const response = await fetch(`${API}/calendar?start=${start.toISOString()}&end=${end.toISOString()}`, { credentials: "include" });
    
    // Sequence guard: only apply if this is still the latest request
    if (thisSeq !== fetchSeqRef.current) {
      console.log('[reloadEvents] Stale response discarded', { thisSeq, currentSeq: fetchSeqRef.current });
      return;
    }
    
    if (response.ok) {
      const data = await response.json();
      console.log('[reloadEvents] Applied', { seq: thisSeq, eventCount: data.events?.length || 0 });
      setEvents(data.events || []);
    }
    // Non-ok response is logged but doesn't throw
  }, [currentDate, viewMode, viewInitialized]);

  const reloadJobs = useCallback(async () => {
    const response = await fetch(`${API}/jobs?status=draft&status=scheduled&status=in_progress`, { credentials: "include" });
    if (response.ok) {
      const data = await response.json();
      setJobs(data.jobs || []);
    }
  }, []);

  const reloadAssignableTechnicians = useCallback(async () => {
    const response = await fetch(`${API}/technicians/assignable`, { credentials: "include" });
    if (response.ok) {
      const data = await response.json();
      setTechnicians(data.technicians || []);
    }
  }, []);

  const reloadAllTechnicians = useCallback(async () => {
    const response = await fetch(`${API}/technicians`, { credentials: "include" });
    if (response.ok) {
      const data = await response.json();
      setAllTechnicians(data.technicians || []);
    }
  }, []);

  const fetchData = useCallback(async () => {
    // Use safe sequential reloads - failures are logged but never throw
    // This ensures UI never freezes due to reload failures
    await safeReload('events', reloadEvents);
    await safeReload('jobs', reloadJobs);
    await safeReload('assignableTechnicians', reloadAssignableTechnicians);
    await safeReload('allTechnicians', reloadAllTechnicians);
    setLoading(false);
  }, [reloadEvents, reloadJobs, reloadAssignableTechnicians, reloadAllTechnicians]);

  useEffect(() => {
    // Only fetch after view is initialized from localStorage
    if (!viewInitialized) return;
    fetchData();
  }, [fetchData, viewInitialized]);

  const navigate = (direction) => {
    if (viewMode === "month") {
      setCurrentDate(prev => direction > 0 ? addMonths(prev, 1) : subMonths(prev, 1));
    } else if (viewMode === "week") {
      setCurrentDate(prev => direction > 0 ? addWeeks(prev, 1) : subWeeks(prev, 1));
    } else {
      setCurrentDate(prev => direction > 0 ? addDays(prev, 1) : subDays(prev, 1));
    }
  };

  const getDateRangeLabel = () => {
    if (viewMode === "month") {
      return format(currentDate, "MMMM yyyy");
    } else if (viewMode === "week") {
      const start = startOfWeek(currentDate);
      const end = endOfWeek(currentDate);
      return `${format(start, "MMM d")} - ${format(end, "MMM d, yyyy")}`;
    } else {
      return format(currentDate, "EEEE, MMMM d, yyyy");
    }
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();

    const days = [];

    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({ date: new Date(year, month - 1, daysInPrevMonth - i), isCurrentMonth: false });
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }

    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }

    return days;
  };

  const getWeekDays = () => {
    const start = startOfWeek(currentDate);
    return eachDayOfInterval({ start, end: endOfWeek(currentDate) });
  };

  const getEventsForDate = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return events.filter((event) => {
      if (!event.start) return false;
      return event.start.startsWith(dateStr);
    });
  };

  const getEventsForHour = (date, hour, minute = 0) => {
    const companyTimezone = businessHours?.timezone;
    return events.filter((event) => {
      if (!event.start) return false;
      try {
        const eventDate = parseISOInCompanyTimezone(event.start, companyTimezone);
        return isSameDay(eventDate, date) && eventDate.getHours() === hour;
      } catch {
        return false;
      }
    });
  };

  // Get all events for a day (for absolute positioning in time grid)
  const getEventsForDay = (date) => {
    const companyTimezone = businessHours?.timezone;
    return events.filter((event) => {
      if (!event.start) return false;
      try {
        const eventDate = parseISOInCompanyTimezone(event.start, companyTimezone);
        return isSameDay(eventDate, date);
      } catch {
        return false;
      }
    });
  };

  const isToday = (date) => isSameDay(date, nowInCompanyTimezone(businessHours?.timezone));

  const getStatusColor = (status) => {
    const colors = {
      scheduled: "bg-blue-500",
      in_progress: "bg-amber-500",
      completed: "bg-emerald-500",
      canceled: "bg-zinc-400",
    };
    return colors[status] || colors.scheduled;
  };

  const handleDayClick = (date) => {
    setSelectedDate(date);
    setSelectedTime(businessHours.start || "09:00");
    setCreateModalOpen(true);
  };

  const handleEventClick = (event, e) => {
    e.stopPropagation();
    setSelectedEvent(event);
    setDetailModalOpen(true);
  };

  const handleMiniCalendarSelect = (date) => {
    if (date) {
      setCurrentDate(date);
      setMiniCalendarOpen(false);
    }
  };

  // Callback when visit is scheduled from the modal
  const handleVisitScheduled = () => {
    // Background reload - non-blocking
    fetchData().catch(err => console.error('[VISIT_SCHEDULED] Background reload failed:', err));
  };

  // Helper to find all Spanish-speaking techs in selection
  const findSpanishTechIds = (techIds) => {
    if (hasSpanishTranslation()) return []; // Entitled - no Spanish techs need CTA
    
    return techIds.filter(techId => {
      const tech = allTechnicians.find(t => t.technician_id === techId);
      return tech && tech.preferred_language === "es";
    });
  };

  // CTA callbacks (for inline panel inside Visit Detail edit modal)
  const handleCTAContinue = () => {
    console.error("[VISIT SAVE] handleCTAContinue ENTERED", { 
      pendingSaveCallback: !!pendingSaveCallback,
      pendingSaveCallbackType: typeof pendingSaveCallback,
    });
    
    // User clicked "Continue without upgrade" - proceed with save immediately
    setSpanishCTAOpen(false);
    if (pendingSaveCallback) {
      const callback = pendingSaveCallback;
      setPendingSaveCallback(null);
      
      console.error("[VISIT SAVE] handleCTAContinue - Callback retrieved", { 
        callbackType: typeof callback,
        callbackStr: callback?.toString?.()?.substring(0, 100),
      });
      
      // GUARD: Verify callback is a direct callable function
      if (typeof callback !== 'function') {
        console.error('[VISIT SAVE] CRITICAL: pendingSaveCallback is not a function', { 
          type: typeof callback, 
          value: callback 
        });
        setIsSavingVisit(false);
        toast.error('Save failed - please try again');
        return;
      }
      
      if (DEBUG_VISIT_SAVE) {
        console.log('[handleCTAContinue] Executing pending save callback');
      }
      
      // Execute the pending save
      console.error("[VISIT SAVE] handleCTAContinue - Calling callback()");
      callback();
      console.error("[VISIT SAVE] handleCTAContinue - callback() returned");
    } else {
      console.error("[VISIT SAVE] handleCTAContinue - NO pendingSaveCallback!");
    }
  };

  // Cancel & Retry handler for inline watchdog panel
  const handleVisitCancelRetry = () => {
    const opId = visitSaveOpRef.current;
    if (DEBUG_VISIT_SAVE) {
      console.log('[VISIT_SAVE] User requested Cancel & Retry via inline panel', { opId });
    }
    // Abort current request
    visitSaveAbortRef.current?.abort?.();
    // Unlock and reset watchdog state
    setIsSavingVisit(false);
    setVisitWatchdogFired(false);
    // Retry after short delay for abort to propagate
    setTimeout(() => doSaveVisitEdit(), 50);
  };

  const handleCTAUpgrade = () => {
    // User clicked "Upgrade" - close CTA, don't save (component navigates)
    setSpanishCTAOpen(false);
    setPendingSaveCallback(null);
    setSpanishTechIds([]);
  };

  const handleCTARemoveSpanishTechs = () => {
    // User clicked "Use English instead" - remove Spanish techs from edit form, no save
    setSpanishCTAOpen(false);
    setPendingSaveCallback(null);
    
    // Remove Spanish techs from edit form selection
    setEditVisitForm(prev => ({
      ...prev,
      assigned_technicians: prev.assigned_technicians.filter(id => !spanishTechIds.includes(id))
    }));
    setSpanishTechIds([]);
    // Don't save - user needs to pick new techs
  };

  const startVisit = async (visitId) => {
    try {
      const response = await fetch(`${API}/visits/${visitId}/start`, {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        console.log('[VISIT_START_CONFIRMED]', { visitId });
        toast.success("Visit started");
        setDetailModalOpen(false);
        // Background reload - non-blocking
        fetchData().catch(err => console.error('[VISIT_START] Background reload failed:', err));
      }
    } catch (error) {
      toast.error("Failed to start visit");
    }
  };

  const completeVisit = async (visitId) => {
    try {
      const response = await fetch(`${API}/visits/${visitId}/complete`, {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        console.log('[VISIT_COMPLETE_CONFIRMED]', { visitId });
        toast.success("Visit completed");
        setDetailModalOpen(false);
        // Background reload - non-blocking
        fetchData().catch(err => console.error('[VISIT_COMPLETE] Background reload failed:', err));
      }
    } catch (error) {
      toast.error("Failed to complete visit");
    }
  };

  // ========================
  // VISIT EDIT/DELETE HANDLERS
  // ========================

  const openVisitEdit = async () => {
    if (!selectedEvent) return;
    
    // Initialize edit form with visit data
    const visitDate = parseISOInCompanyTimezone(selectedEvent.start, businessHours?.timezone);
    const startTime = format(visitDate, "HH:mm");
    const endTime = selectedEvent.end ? format(parseISOInCompanyTimezone(selectedEvent.end, businessHours?.timezone), "HH:mm") : "10:00";
    
    // CRITICAL FIX: Calendar API returns technicians as array of string IDs ["tech_id_1", "tech_id_2"]
    // NOT as objects [{technician_id: "tech_id_1"}]. Handle both formats for safety.
    let techIds = [];
    if (Array.isArray(selectedEvent.technicians)) {
      // Check if it's array of strings or array of objects
      if (selectedEvent.technicians.length > 0) {
        if (typeof selectedEvent.technicians[0] === 'string') {
          // Already array of IDs (correct format from Calendar API)
          techIds = selectedEvent.technicians;
        } else if (selectedEvent.technicians[0]?.technician_id) {
          // Array of objects (fallback)
          techIds = selectedEvent.technicians.map(t => t.technician_id);
        }
      }
    } else if (Array.isArray(selectedEvent.assigned_technicians)) {
      // JobDetail format - direct array of IDs
      techIds = selectedEvent.assigned_technicians;
    }
    
    setEditVisitForm({
      date: visitDate,
      startTime,
      endTime,
      assigned_technicians: techIds,
      customer_address_id: selectedEvent.customer_address_id || null,
    });
    
    // Fetch customer addresses
    if (selectedEvent.customer_id) {
      try {
        const addrRes = await fetch(`${API}/customers/${selectedEvent.customer_id}/addresses`, { credentials: "include" });
        if (addrRes.ok) {
          const data = await addrRes.json();
          setVisitAddresses(data.addresses || []);
        }
      } catch (error) {
        console.error("Error fetching addresses:", error);
      }
    }
    
    setIsEditingVisit(true);
  };

  const isOutsideBusinessHours = (time) => {
    const [h] = time.split(":").map(Number);
    const [startH] = businessHours.start.split(":").map(Number);
    const [endH] = businessHours.end.split(":").map(Number);
    return h < startH || h >= endH;
  };

  // Save Visit Edit handler - shows CTA if Spanish techs selected
  const saveVisitEdit = (confirmed = false) => {
    // MANDATORY INSTRUMENTATION - LOGS EVERY TIME SAVE IS CLICKED
    console.error("[VISIT SAVE] ENTERED", {
      isSavingVisit,
      selectedEvent: !!selectedEvent,
      selectedEventId: selectedEvent?.id,
      confirmed,
      spanishCTAOpen,
      editVisitForm: {
        assigned_technicians: editVisitForm.assigned_technicians,
        startTime: editVisitForm.startTime,
        endTime: editVisitForm.endTime,
      }
    });
    
    if (!selectedEvent) {
      console.error("[VISIT SAVE] BLOCKED: !selectedEvent");
      return;
    }
    
    const isSpanishTechSelected = findSpanishTechIds(editVisitForm.assigned_technicians).length > 0;
    
    // VISIT_SAVE_START - Debug log at entry point
    if (DEBUG_VISIT_SAVE) {
      console.log('[VISIT_SAVE_START]', { 
        opId: visitSaveOpRef.current,
        visit_id: selectedEvent.id, 
        selected_tech_ids: editVisitForm.assigned_technicians,
        isSpanishTechSelected,
        confirmed,
        startTime: editVisitForm.startTime,
        endTime: editVisitForm.endTime 
      });
    }
    
    // Skip Spanish check if this is a confirmed override (e.g., outside hours)
    // P1 STABILITY LOCKDOWN: Spanish CTA must render inline when blocking - do not modify without reopening stability review
    if (!confirmed) {
      const spanishIds = findSpanishTechIds(editVisitForm.assigned_technicians);
      
      if (spanishIds.length > 0) {
        console.error("[VISIT SAVE] BLOCKED: Spanish CTA triggered", { spanishIds });
        // Spanish techs selected without entitlement - show CTA
        setSpanishTechIds(spanishIds);
        // Store callback using .bind() pattern (same as JOB save in JobDetail.js)
        setPendingSaveCallback(() => doSaveVisitEdit.bind(null));
        setSpanishCTAOpen(true);
        if (DEBUG_VISIT_SAVE) {
          console.log('[VISIT_SAVE] Spanish CTA shown, pending callback stored', { spanishIds });
        }
        return; // Don't save yet - wait for CTA decision
      }
    }
    
    // Check for outside business hours (frontend check)
    if (!confirmed && (isOutsideBusinessHours(editVisitForm.startTime) || isOutsideBusinessHours(editVisitForm.endTime))) {
      if (DEBUG_VISIT_SAVE) {
        console.log('[VISIT_SAVE] Frontend outside-hours check triggered, showing warning', { 
          startTime: editVisitForm.startTime, 
          endTime: editVisitForm.endTime 
        });
      }
      setShowOutsideHoursWarning(true);
      return;
    }
    
    // No Spanish techs or confirmed - proceed with save
    if (DEBUG_VISIT_SAVE) {
      console.log('[VISIT_SAVE] Proceeding to doSaveVisitEdit', { confirmed });
    }
    doSaveVisitEdit(confirmed);
  };

  // Actual save implementation - only called when CTA check passes
  const doSaveVisitEdit = async (confirmed = false) => {
    if (DEBUG_VISIT_SAVE) {
      console.log('[VISIT_SAVE] doSaveVisitEdit entered', {
        isSavingVisit,
        confirmed,
        selectedEventId: selectedEvent?.id,
      });
    }
    
    // Prevent double-clicks while already saving
    if (isSavingVisit) {
      if (DEBUG_VISIT_SAVE) {
        console.log('[VISIT_SAVE] Already saving, ignoring duplicate call');
      }
      return;
    }
    
    // Abort any prior in-flight request and start new operation
    visitSaveAbortRef.current?.abort?.();
    visitSaveOpRef.current += 1;
    const opId = visitSaveOpRef.current;
    const abortController = new AbortController();
    visitSaveAbortRef.current = abortController;
    
    setIsSavingVisit(true);
    setVisitWatchdogFired(false); // Reset watchdog state for new operation
    
    if (DEBUG_VISIT_SAVE) {
      console.log('[VISIT_SAVE_REQUEST_START]', { 
        opId, 
        endpoint: `${API}/visits/${selectedEvent?.id}/reschedule`,
        isSavingVisit: true 
      });
    }
    
    // 20-SECOND HARD TIMEOUT: Aborts request and unlocks UI (prevents indefinite hang)
    // P1 STABILITY LOCKDOWN: Do not modify timeout/watchdog/unlock logic without reopening stability review
    const SAVE_TIMEOUT_MS = 20000;
    const timeoutId = setTimeout(() => {
      // Only timeout if this operation is still current
      if (opId === visitSaveOpRef.current) {
        if (DEBUG_VISIT_SAVE) {
          console.warn('[VISIT_SAVE_TIMEOUT]', {
            opId,
            visitId: selectedEvent?.id,
            message: 'Save timed out after 20s - aborting and unlocking'
          });
        }
        abortController.abort('timeout');
        // Toast will be shown in catch block via AbortError handling
      }
    }, SAVE_TIMEOUT_MS);
    
    // 12-SECOND WATCHDOG: Shows toast AND inline panel but does NOT unlock UI (prevents double-submit)
    const watchdogTimeout = setTimeout(() => {
      // Only show watchdog if this operation is still current
      if (opId === visitSaveOpRef.current) {
        if (DEBUG_VISIT_SAVE) {
          console.warn('[VISIT_SAVE_WATCHDOG]', {
            opId,
            visitId: selectedEvent?.id,
            assignedTechs: editVisitForm.assigned_technicians,
            message: 'Save taking >12s - NOT unlocking'
          });
        }
        // Set inline watchdog panel state
        setVisitWatchdogFired(true);
        // Also show toast for visibility
        toast('Still working…', {
          description: 'You can keep waiting, or cancel and retry.',
          action: {
            label: 'Cancel & Retry',
            onClick: () => {
              // Abort current request and retry
              if (opId === visitSaveOpRef.current) {
                if (DEBUG_VISIT_SAVE) {
                  console.log('[VISIT_SAVE] User requested Cancel & Retry via toast', { opId });
                }
                abortController.abort('user_cancel');
                // Unlock and retry after short delay for abort to propagate
                setIsSavingVisit(false);
                setVisitWatchdogFired(false);
                setTimeout(() => doSaveVisitEdit(confirmed), 50);
              }
            },
          },
          duration: 10000,
        });
      }
    }, 12000);
    
    try {
      // Convert user-selected times (in company timezone) to UTC
      const companyTimezone = businessHours?.timezone;
      
      // Handle overnight visits: if end time is earlier than start time,
      // the visit crosses midnight and end date should be the next day
      const [startH, startM] = editVisitForm.startTime.split(":").map(Number);
      const [endH, endM] = editVisitForm.endTime.split(":").map(Number);
      const startMinutes = startH * 60 + startM;
      const endMinutes = endH * 60 + endM;
      const isOvernight = endMinutes < startMinutes;
      
      let endDate = editVisitForm.date;
      if (isOvernight) {
        endDate = addDays(new Date(editVisitForm.date), 1);
      }
      
      // Convert times to UTC - this may throw if time is in DST gap
      let utcStart, utcEnd;
      try {
        utcStart = localTimeToUTCISO(editVisitForm.date, editVisitForm.startTime, companyTimezone);
        utcEnd = localTimeToUTCISO(endDate, editVisitForm.endTime, companyTimezone);
      } catch (timeError) {
        if (timeError.isDSTGapError) {
          // DST gap - show user-friendly error
          toast.error(timeError.message, { duration: 8000 });
          return; // Exit - finally will unlock UI
        }
        throw timeError; // Re-throw other errors
      }
      
      const payload = {
        scheduled_start: utcStart,
        scheduled_end: utcEnd,
        assigned_technicians: editVisitForm.assigned_technicians,
        customer_address_id: editVisitForm.customer_address_id,
        override_business_hours: confirmed,
      };
      
      // === DIAGNOSTIC CHECKPOINT E2: Exact outgoing payload ===
      console.log('[DIAG-E2] Outgoing payload:', JSON.stringify(payload, null, 2));
      
      if (DEBUG_VISIT_SAVE) {
        console.log('[VISIT_SAVE_REQUEST]', {
          opId,
          url: `${API}/visits/${selectedEvent.id}/reschedule`,
          payload,
          timezone: companyTimezone,
          isOvernight,
          hasAbortSignal: !!abortController.signal
        });
      }
      
      const response = await fetch(`${API}/visits/${selectedEvent.id}/reschedule`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
        signal: abortController.signal,
      });
      
      // Read response body exactly once using safe helper
      const parsed = await safeReadBody(response);
      
      // === DIAGNOSTIC CHECKPOINT H: Exact API response ===
      console.log('[DIAG-H] API response:', {
        status: response.status,
        ok: response.ok,
        'scheduled_start': parsed.data?.scheduled_start,
        'scheduled_end': parsed.data?.scheduled_end,
        fullData: parsed.data
      });
      
      if (DEBUG_VISIT_SAVE) {
        console.log('[VISIT_SAVE_SUCCESS]', {
          opId,
          status: response.status,
          ok: response.ok,
          data: parsed.data,
          rawLength: parsed.raw?.length
        });
      }
      
      if (response.ok) {
        // WRITE-SUCCESS GUARD: Log confirmation BEFORE any secondary operations
        console.log('[VISIT_SAVE_CONFIRMED]', { opId, visitId: selectedEvent?.id });
        
        // Show success immediately
        toast.success("Visit updated");
        
        // Close modal and reset UI state IMMEDIATELY (before reloads)
        // User should never feel blocked by data refresh
        setIsEditingVisit(false);
        setShowOutsideHoursWarning(false);
        setSpanishTechIds([]); // Clear Spanish tech IDs
        setSpanishCTAOpen(false); // Ensure CTA is closed
        setDetailModalOpen(false);
        
        // Background reload - failures are isolated and non-blocking
        // Using fire-and-forget pattern so UI is already unlocked
        fetchData().catch(err => {
          if (DEBUG_VISIT_SAVE) {
            console.error('[VISIT_SAVE] Background reload failed (non-fatal):', err);
          }
        });
      } else if (response.status === 422 && isOutsideHoursWarning(parsed)) {
        // Backend says outside business hours - show warning
        if (DEBUG_VISIT_SAVE) {
          console.log('[VISIT_SAVE] 422 outside-hours warning from backend, showing warning dialog');
        }
        setShowOutsideHoursWarning(true);
      } else {
        // Handle structured backend error (VISIT_SAVE_FAILED) or generic error
        let errorMsg;
        if (parsed.data?.error === 'VISIT_SAVE_FAILED') {
          // Structured error from our try/catch wrapper
          errorMsg = parsed.data.message || 'Visit could not be saved. Please retry.';
        } else {
          // Generic error message extraction
          errorMsg = bestErrorMessage(response, parsed);
        }
        if (DEBUG_VISIT_SAVE) {
          console.error('[VISIT_SAVE_ERROR]', { opId, errorMsg, parsed });
        }
        toast.error(errorMsg);
      }
    } catch (error) {
      // Handle abort - distinguish between timeout and user cancel
      if (error.name === 'AbortError') {
        // Check if this was a timeout abort (reason will be 'timeout')
        const isTimeout = abortController.signal.reason === 'timeout';
        if (isTimeout) {
          if (DEBUG_VISIT_SAVE) {
            console.warn('[VISIT_SAVE] Request aborted due to timeout', { opId });
          }
          toast.error('Save timed out. Please retry.');
        } else {
          // User-initiated abort (Cancel & Retry) - don't show error
          if (DEBUG_VISIT_SAVE) {
            console.log('[VISIT_SAVE] Request aborted by user', { opId });
          }
        }
        return; // Exit early - finally will still run to unlock UI
      }
      // Network error or fetch threw
      if (DEBUG_VISIT_SAVE) {
        console.error('[VISIT_SAVE_ERROR]', { opId, error: error.message });
      }
      toast.error(`Network error: ${error.message || "Connection failed"}`);
    } finally {
      // P1 STABILITY LOCKDOWN: UI unlock MUST always run - do not add conditions without reopening stability review
      // Clear BOTH timeouts
      clearTimeout(timeoutId);
      clearTimeout(watchdogTimeout);
      // ALWAYS unlock isSavingVisit - no conditions
      // The stale operation check was causing issues where the UI stayed locked
      setIsSavingVisit(false);
      setVisitWatchdogFired(false);
      if (DEBUG_VISIT_SAVE) {
        console.log('[VISIT_SAVE_FINALLY]', { opId, currentOpId: visitSaveOpRef.current, unlocked: true });
      }
    }
  };

  const deleteVisit = async () => {
    if (!selectedEvent) return;
    
    try {
      const response = await fetch(`${API}/visits/${selectedEvent.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      
      if (response.ok) {
        console.log('[VISIT_DELETE_CONFIRMED]', { visitId: selectedEvent.id });
        toast.success("Visit deleted");
        setShowDeleteVisitConfirm(false);
        setDetailModalOpen(false);
        // Background reload - non-blocking
        fetchData().catch(err => console.error('[VISIT_DELETE] Background reload failed:', err));
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete visit");
      }
    } catch (error) {
      toast.error("Failed to delete visit");
    }
  };

  // ========================
  // DRAG & DROP HANDLERS
  // ========================
  
  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
    const dragEvent = events.find(e => e.id === active.id);
    setDraggedEvent(dragEvent);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    setDraggedEvent(null);

    if (!over || !active) return;

    const visitId = active.id;
    const dropData = over.id; // Format: "slot-{date}-{hour}-{minute}" or "day-{date}"
    
    // Find the dragged event
    const draggedVisit = events.find(e => e.id === visitId);
    if (!draggedVisit) return;

    // Only allow rescheduling scheduled visits
    if (draggedVisit.status !== "scheduled") {
      toast.error(`Cannot reschedule a ${draggedVisit.status} visit`);
      return;
    }

    // Parse the drop target
    let newStartDate;
    let newStartHour;
    let newStartMinute;

    if (dropData.startsWith("slot-")) {
      // Week/Day view drop: slot-2026-01-15-9-0
      const parts = dropData.replace("slot-", "").split("-");
      const year = parseInt(parts[0]);
      const month = parseInt(parts[1]) - 1;
      const day = parseInt(parts[2]);
      newStartHour = parseInt(parts[3]);
      newStartMinute = parseInt(parts[4] || 0);
      newStartDate = new Date(year, month, day, newStartHour, newStartMinute);
    } else if (dropData.startsWith("day-")) {
      // Month view drop: day-2026-01-15
      const dateStr = dropData.replace("day-", "");
      const parts = dateStr.split("-");
      const year = parseInt(parts[0]);
      const month = parseInt(parts[1]) - 1;
      const day = parseInt(parts[2]);
      
      // Preserve original time for month view drops (parse in company timezone)
      const companyTimezone = businessHours?.timezone;
      const originalStart = parseISOInCompanyTimezone(draggedVisit.start, companyTimezone);
      newStartHour = originalStart.getHours();
      newStartMinute = originalStart.getMinutes();
      newStartDate = new Date(year, month, day, newStartHour, newStartMinute);
    } else {
      return;
    }

    // Calculate duration from original visit (parse in company timezone)
    const companyTimezone = businessHours?.timezone;
    const originalStart = parseISOInCompanyTimezone(draggedVisit.start, companyTimezone);
    const originalEnd = draggedVisit.end ? parseISOInCompanyTimezone(draggedVisit.end, companyTimezone) : addDays(originalStart, 0);
    const durationMinutes = differenceInMinutes(originalEnd, originalStart);
    
    // Calculate new end time
    const newEndDate = new Date(newStartDate.getTime() + durationMinutes * 60000);

    console.log('[Drag] Drop:', {
      visitId,
      dropTarget: dropData,
      newStart: newStartDate.toISOString(),
      newEnd: newEndDate.toISOString(),
      duration: durationMinutes
    });

    // Use unified update function
    await updateVisitTimes(visitId, newStartDate, newEndDate, 'drag');
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setDraggedEvent(null);
  };

  // ========================
  // UNIFIED VISIT UPDATE FUNCTION
  // ========================
  // Both resize and drag use this single function
  // CONTRACT: UI must never display a state that was not successfully persisted
  const updateVisitTimes = useCallback(async (visitId, newStart, newEnd, action = 'update') => {
    // Find the visit
    const visit = events.find(e => e.id === visitId);
    if (!visit) {
      console.error('[UpdateVisit] Visit not found:', visitId);
      return false;
    }

    // Store original values for potential debug/logging
    const originalStart = visit.start;
    const originalEnd = visit.end;
    
    // Convert company-timezone dates back to UTC for storage
    const companyTimezone = businessHours?.timezone;
    const utcStart = toUTCISO(newStart, companyTimezone);
    const utcEnd = toUTCISO(newEnd, companyTimezone);

    // Log the update request
    console.log(`[UpdateVisit] ${action}:`, {
      visitId,
      originalStart,
      originalEnd,
      newStart: utcStart,
      newEnd: utcEnd,
      companyTimezone
    });

    // NO OPTIMISTIC UPDATE - we wait for server confirmation
    // This ensures UI never shows unpersisted state

    // Persist to backend
    try {
      const payload = {
        scheduled_start: utcStart,
        scheduled_end: utcEnd,
      };
      
      console.log('[UpdateVisit] API payload:', payload);
      
      const response = await fetch(`${API}/visits/${visitId}/reschedule`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const savedVisit = await response.json();
        const duration = differenceInMinutes(newEnd, newStart);
        
        console.log('[UpdateVisit] Server confirmed:', savedVisit);
        
        // Update UI with the ACTUAL server response - single source of truth
        // Force re-render with new updatedAt timestamp
        const serverUpdatedAt = Date.now();
        setEvents(prev => prev.map(e => 
          e.id === visitId 
            ? { ...e, start: savedVisit.scheduled_start, end: savedVisit.scheduled_end, updatedAt: serverUpdatedAt }
            : e
        ));
        
        toast.success(`Visit ${action === 'resize' ? 'resized' : 'rescheduled'} (${duration} min)`);
        return true;
      } else {
        // Server rejected the change - show error, UI stays at original state
        const error = await response.json();
        console.error('[UpdateVisit] Server rejected:', error);
        
        // Display clear error explaining why
        const errorMessage = error.detail || `Failed to ${action} visit`;
        toast.error(errorMessage);
        
        // Force UI to re-render at original position (in case of visual glitch)
        setEvents(prev => prev.map(e => 
          e.id === visitId 
            ? { ...e, start: originalStart, end: originalEnd, updatedAt: Date.now() }
            : e
        ));
        return false;
      }
    } catch (error) {
      console.error('[UpdateVisit] Network error:', error);
      
      toast.error(`Network error: Failed to ${action} visit`);
      // Force UI to re-render at original position
      setEvents(prev => prev.map(e => 
        e.id === visitId 
          ? { ...e, start: originalStart, end: originalEnd, updatedAt: Date.now() }
          : e
      ));
      return false;
    }
  }, [events, businessHours?.timezone]);

  // ========================
  // RESIZE HANDLER (uses unified update)
  // ========================
  const handleResize = useCallback(async (visitId, newStart, newEnd) => {
    console.log('[Resize Handler] Called:', { visitId, newStart, newEnd });
    await updateVisitTimes(visitId, newStart, newEnd, 'resize');
  }, [updateVisitTimes]);

  const days = getDaysInMonth();
  const weekDays = getWeekDays();

  // Show loading skeleton until view is initialized
  if (!viewInitialized) {
    return (
      <div className="animate-pulse space-y-6" data-testid="calendar-loading">
        <div className="h-12 bg-zinc-200 rounded-sm w-1/3" />
        <div className="h-96 bg-zinc-200 rounded-sm" />
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      {/* First-value onboarding success overlay */}
      {showFVOSuccessOverlay && <GuidedCalendarSuccessOverlay />}
      
      <div className="space-y-6 max-w-full overflow-x-hidden" data-testid="calendar-page">
        {/* Header */}
        <div className="flex flex-col gap-4">
          {/* Navigation row */}
          <div className="flex items-center justify-center sm:justify-start gap-2 sm:gap-4">
            <Button variant="outline" size="icon" onClick={() => navigate(-1)} className="border-2 flex-shrink-0" data-testid="prev-btn">
              <CaretLeft size={20} />
            </Button>
            <h2 className="font-heading text-lg sm:text-xl font-semibold uppercase text-center min-w-0">
              {getDateRangeLabel()}
            </h2>
            <Button variant="outline" size="icon" onClick={() => navigate(1)} className="border-2 flex-shrink-0" data-testid="next-btn">
              <CaretRight size={20} />
            </Button>
          </div>
          
          {/* Controls row - scrollable on mobile */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
            {/* View Mode Toggle - Segmented Control Style */}
            <div className="flex items-center bg-white border border-zinc-200 rounded-lg overflow-hidden flex-shrink-0">
              <button
                onClick={() => handleViewModeChange("day")}
                className={`flex items-center px-3 py-2 text-sm font-medium transition-colors border-r border-zinc-200
                  ${viewMode === "day" 
                    ? "bg-[#FFF4EC] text-[#FF6600] border-b-2 border-b-[#FF6600]" 
                    : "text-zinc-600 hover:bg-zinc-50"
                  }`}
                data-testid="day-view-btn"
              >
                <CalendarIcon size={16} className="mr-1.5" />
                Day
              </button>
              <button
                onClick={() => handleViewModeChange("week")}
                className={`flex items-center px-3 py-2 text-sm font-medium transition-colors border-r border-zinc-200
                  ${viewMode === "week" 
                    ? "bg-[#FFF4EC] text-[#FF6600] border-b-2 border-b-[#FF6600]" 
                    : "text-zinc-600 hover:bg-zinc-50"
                  }`}
                data-testid="week-view-btn"
              >
                <Rows size={16} className="mr-1.5" />
                Week
              </button>
              <button
                onClick={() => handleViewModeChange("month")}
                className={`flex items-center px-3 py-2 text-sm font-medium transition-colors
                  ${viewMode === "month" 
                    ? "bg-[#FFF4EC] text-[#FF6600] border-b-2 border-b-[#FF6600]" 
                    : "text-zinc-600 hover:bg-zinc-50"
                  }`}
                data-testid="month-view-btn"
              >
                <SquaresFour size={16} className="mr-1.5" />
                Month
              </button>
            </div>

            <Button onClick={() => { setSelectedDate(new Date()); setSelectedTime(businessHours.start || "09:00"); setCreateModalOpen(true); }} className="bg-[#FF6600] hover:bg-[#CC5200] text-white flex-shrink-0 whitespace-nowrap" data-testid="new-visit-btn">
              <Plus size={18} className="mr-2" />
              <span className="hidden sm:inline">Schedule Visit</span>
              <span className="sm:hidden">New</span>
            </Button>
          </div>
        </div>

        {/* Calendar Views */}
        {viewMode === "month" && (
          <Card className="border-2 border-zinc-200 overflow-hidden">
            <CardContent className="p-0">
              {/* Day Headers */}
              <div className="grid grid-cols-7 border-b border-zinc-200">
                {DAYS.map((day) => (
                  <div key={day} className="p-3 text-center text-sm font-medium text-zinc-500 bg-zinc-50 border-r border-zinc-100 last:border-r-0">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Days */}
              <div className="grid grid-cols-7">
                {days.map((day, idx) => {
                  const dayEvents = getEventsForDate(day.date);
                  const today = isToday(day.date);
                  const dropId = `day-${format(day.date, "yyyy-MM-dd")}`;
                  
                  return (
                    <DroppableSlot
                      key={idx}
                      id={dropId}
                      onClick={() => handleDayClick(day.date)}
                      className={`min-h-[100px] md:min-h-[120px] p-2 border-b border-r border-zinc-100 cursor-pointer transition-colors hover:bg-zinc-50 ${
                        !day.isCurrentMonth ? "bg-zinc-50/50" : ""
                      } ${today ? "bg-[#FF6600]/5" : ""}`}
                    >
                      <div className={`text-sm font-medium mb-1 w-7 h-7 flex items-center justify-center rounded-full ${
                        !day.isCurrentMonth ? "text-zinc-300" : today ? "bg-[#FF6600] text-white" : "text-zinc-700"
                      }`}>
                        {day.date.getDate()}
                      </div>
                      <div className="space-y-1">
                        {dayEvents.slice(0, 3).map((event, eventIdx) => (
                          <DraggableEvent key={event.id} event={event} viewMode="month" disabled={isMobileDevice || event.status !== "scheduled"}>
                            <div
                              onClick={(e) => handleEventClick(event, e)}
                              className={`text-xs p-1.5 rounded-sm text-white truncate cursor-pointer hover:opacity-90 ${getStatusColor(event.status)} ${
                                event.status !== "scheduled" ? "opacity-75" : ""
                              }`}
                              title={`${event.title} - ${event.customer}${event.status !== "scheduled" ? " (Cannot drag)" : isMobileDevice ? "" : " (Drag to reschedule)"}`}
                              data-testid={`event-${event.id}`}
                            >
                              <span className="font-medium">{event.start ? format(parseISOInCompanyTimezone(event.start, businessHours?.timezone), "h:mm a") : ""}</span>
                              <span className="ml-1 opacity-90">{event.title}</span>
                            </div>
                          </DraggableEvent>
                        ))}
                        {dayEvents.length > 3 && (
                          <div className="text-xs text-zinc-400 pl-1">+{dayEvents.length - 3} more</div>
                        )}
                      </div>
                    </DroppableSlot>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {viewMode === "week" && (
          <Card className="border-2 border-zinc-200 overflow-hidden">
            <CardContent className="p-0">
              {/* Day Headers for Week View */}
              <div className="grid border-b border-zinc-200" style={{ gridTemplateColumns: "4rem repeat(7, 1fr)" }}>
                <div className="p-3 text-center text-sm font-medium text-zinc-400 bg-zinc-50 border-r border-zinc-100">
                  Time
                </div>
                {weekDays.map((day, idx) => (
                  <div 
                    key={idx} 
                    className={`p-3 text-center border-r border-zinc-100 last:border-r-0 ${isToday(day) ? "bg-[#FF6600]/10" : "bg-zinc-50"}`}
                  >
                    <div className="text-xs text-zinc-500">{format(day, "EEE")}</div>
                    <div className={`text-lg font-semibold ${isToday(day) ? "text-[#FF6600]" : "text-zinc-700"}`}>
                      {format(day, "d")}
                    </div>
                  </div>
                ))}
              </div>

              {/* Before Open Strips Row */}
              <div className="grid border-b border-zinc-100" style={{ gridTemplateColumns: "4rem repeat(7, 1fr)" }}>
                <div className="bg-zinc-50/50 border-r border-zinc-100" />
                {weekDays.map((day, dayIdx) => {
                  const dayKey = format(day, "yyyy-MM-dd");
                  const dayEvents = getEventsForDay(day);
                  const { beforeOpenItems } = categorizeEventsByBusinessHours(
                    dayEvents, 
                    businessHours.start, 
                    businessHours.end, 
                    businessHours?.timezone
                  );
                  
                  return (
                    <div key={dayIdx} className="border-r border-zinc-100 last:border-r-0 p-1">
                      <OffHoursStrip
                        items={beforeOpenItems}
                        label="Before open"
                        type="before"
                        dayKey={dayKey}
                        onItemClick={(item) => {
                          setSelectedEvent(item);
                          setDetailModalOpen(true);
                        }}
                      />
                    </div>
                  );
                })}
              </div>

              {/* Time Grid with Absolute Event Positioning */}
              <div className="grid" style={{ gridTemplateColumns: "4rem repeat(7, 1fr)" }}>
                {/* Time labels column */}
                <div className="bg-zinc-50/50 border-r border-zinc-100">
                  {timeSlots.map((slot) => (
                    <div 
                      key={`${slot.hour}-${slot.minute}`} 
                      className={`text-xs text-zinc-400 text-right pr-3 ${slot.minute === 0 ? 'border-b border-zinc-100' : 'border-b border-zinc-50'}`}
                      style={{ height: `${SLOT_HEIGHT_PX / (60 / RESIZE_GRID_MINUTES)}px` }}
                    >
                      {/* Only show label at hour marks */}
                      {slot.minute === 0 && <span className="block pt-2">{slot.label}</span>}
                    </div>
                  ))}
                </div>
                
                {/* Day columns with absolute positioned events */}
                {weekDays.map((day, dayIdx) => {
                  const dayEvents = getEventsForDay(day);
                  // Categorize events by business hours
                  const { gridEvents } = categorizeEventsByBusinessHours(
                    dayEvents, 
                    businessHours.start, 
                    businessHours.end, 
                    businessHours?.timezone
                  );
                  // Calculate overlap layout for grid events only
                  const overlapLayoutMap = calculateOverlapLayout(gridEvents);
                  
                  return (
                    <div 
                      key={dayIdx} 
                      className={`relative overflow-hidden border-r border-zinc-100 last:border-r-0 ${isToday(day) ? "bg-[#FF6600]/5" : ""}`}
                    >
                      {/* Drop target grid lines */}
                      {timeSlots.map((slot) => {
                        const dropId = `slot-${format(day, "yyyy-MM-dd")}-${slot.hour}-${slot.minute}`;
                        return (
                          <DroppableSlot
                            key={`${slot.hour}-${slot.minute}`}
                            id={dropId}
                            onClick={() => {
                              const dateWithTime = new Date(day);
                              dateWithTime.setHours(slot.hour, slot.minute, 0, 0);
                              setSelectedDate(dateWithTime);
                              setSelectedTime(`${slot.hour.toString().padStart(2, '0')}:${slot.minute.toString().padStart(2, '0')}`);
                              setCreateModalOpen(true);
                            }}
                            className={`${slot.minute === 0 ? 'border-b border-zinc-100' : 'border-b border-zinc-50'} cursor-pointer hover:bg-zinc-100/50`}
                            style={{ height: `${SLOT_HEIGHT_PX / (60 / RESIZE_GRID_MINUTES)}px` }}
                          >
                            {/* Empty - events are positioned absolutely */}
                          </DroppableSlot>
                        );
                      })}
                      
                      {/* Absolutely positioned events (grid events only - clamped to business hours) */}
                      {gridEvents.map((event) => {
                        const layout = overlapLayoutMap.get(event.id);
                        // Use clamped start/end if available for rendering
                        const renderEvent = {
                          ...event,
                          start: event.clampedStart || event.start,
                          end: event.clampedEnd || event.end,
                        };
                        return (
                          <ResizableEvent 
                            key={`${event.id}-${event.start}-${event.end}`} 
                            event={renderEvent} 
                            viewMode="week"
                            businessHours={businessHours}
                            onResize={handleResize}
                            onClick={(e) => handleEventClick(event, e)}
                            canResize={!isMobileDevice && event.status === "scheduled"}
                            renderWithHeight={true}
                            overlapLayout={layout}
                          >
                            {({ displayStart, displayEnd, isResizing }) => (
                              <DraggableEvent event={event} viewMode="week" disabled={isMobileDevice || event.status !== "scheduled"}>
                                <div
                                  className={`h-full text-xs p-1 rounded-sm text-white cursor-pointer hover:opacity-90 overflow-hidden ${getStatusColor(event.status)} ${
                                    event.status !== "scheduled" ? "opacity-75" : ""
                                  } ${isResizing ? "ring-2 ring-[#FF6600]" : ""}`}
                                  title={event.status !== "scheduled" ? `Cannot drag (${event.status})` : isMobileDevice ? "Tap to view details" : "Drag to reschedule or resize edges"}
                                  data-testid={`week-event-${event.id}`}
                                >
                                  <div className="flex items-center gap-1">
                                    {event.status === "scheduled" && !isMobileDevice && (
                                      <DotsSixVertical size={10} className="opacity-60 flex-shrink-0" />
                                    )}
                                    <div className="font-medium truncate flex-1 text-[10px]">{event.title}</div>
                                  </div>
                                  <div className="opacity-80 truncate text-[9px]">{event.customer}</div>
                                  {displayStart && displayEnd && (
                                    <div className="opacity-70 text-[9px]">
                                      {format(displayStart, "h:mm")} - {format(displayEnd, "h:mm a")}
                                    </div>
                                  )}
                                </div>
                              </DraggableEvent>
                            )}
                          </ResizableEvent>
                        );
                      })}
                    </div>
                  );
                })}
              </div>

              {/* After Close Strips Row */}
              <div className="grid border-t border-zinc-100" style={{ gridTemplateColumns: "4rem repeat(7, 1fr)" }}>
                <div className="bg-zinc-50/50 border-r border-zinc-100" />
                {weekDays.map((day, dayIdx) => {
                  const dayKey = format(day, "yyyy-MM-dd");
                  const dayEvents = getEventsForDay(day);
                  const { afterCloseItems } = categorizeEventsByBusinessHours(
                    dayEvents, 
                    businessHours.start, 
                    businessHours.end, 
                    businessHours?.timezone
                  );
                  
                  return (
                    <div key={dayIdx} className="border-r border-zinc-100 last:border-r-0 p-1">
                      <OffHoursStrip
                        items={afterCloseItems}
                        label="After close"
                        type="after"
                        dayKey={dayKey}
                        onItemClick={(item) => {
                          setSelectedEvent(item);
                          setDetailModalOpen(true);
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {viewMode === "day" && (
          <Card className="border-2 border-zinc-200 overflow-hidden">
            <CardContent className="p-0">
              {/* Day Header */}
              <div className={`p-4 border-b border-zinc-200 ${isToday(currentDate) ? "bg-[#FF6600]/10" : "bg-zinc-50"}`}>
                <div className="text-center">
                  <div className="text-sm text-zinc-500">{format(currentDate, "EEEE")}</div>
                  <div className={`text-3xl font-bold ${isToday(currentDate) ? "text-[#FF6600]" : "text-zinc-700"}`}>
                    {format(currentDate, "MMMM d, yyyy")}
                  </div>
                </div>
              </div>

              {/* Before Open Strip for Day View */}
              {(() => {
                const dayKey = format(currentDate, "yyyy-MM-dd");
                const dayEvents = getEventsForDay(currentDate);
                const { beforeOpenItems } = categorizeEventsByBusinessHours(
                  dayEvents, 
                  businessHours.start, 
                  businessHours.end, 
                  businessHours?.timezone
                );
                
                if (beforeOpenItems.length === 0) return null;
                
                return (
                  <div className="px-4 py-2 border-b border-zinc-100 bg-zinc-50/50">
                    <OffHoursStrip
                      items={beforeOpenItems}
                      label="Before open"
                      type="before"
                      dayKey={dayKey}
                      onItemClick={(item) => {
                        setSelectedEvent(item);
                        setDetailModalOpen(true);
                      }}
                    />
                  </div>
                );
              })()}

              {/* Time Grid for Day View with Absolute Positioning */}
              <div className="flex overflow-hidden">
                {/* Time labels column */}
                <div className="w-20 bg-zinc-50/50 border-r border-zinc-100 flex-shrink-0">
                  {timeSlots.map((slot) => (
                    <div 
                      key={`${slot.hour}-${slot.minute}`} 
                      className={`text-sm text-zinc-400 text-right pr-3 ${slot.minute === 0 ? 'border-b border-zinc-100' : 'border-b border-zinc-50'}`}
                      style={{ height: `${DAY_SLOT_HEIGHT_PX / (60 / RESIZE_GRID_MINUTES)}px` }}
                    >
                      {/* Only show label at hour marks */}
                      {slot.minute === 0 && <span className="block pt-3">{slot.label}</span>}
                    </div>
                  ))}
                </div>
                
                {/* Events column with absolute positioning */}
                <div className="flex-1 relative overflow-hidden">
                  {/* Drop target grid lines */}
                  {timeSlots.map((slot) => {
                    const dropId = `slot-${format(currentDate, "yyyy-MM-dd")}-${slot.hour}-${slot.minute}`;
                    return (
                      <DroppableSlot
                        key={`${slot.hour}-${slot.minute}`}
                        id={dropId}
                        onClick={() => {
                          const dateWithTime = new Date(currentDate);
                          dateWithTime.setHours(slot.hour, slot.minute, 0, 0);
                          setSelectedDate(dateWithTime);
                          setSelectedTime(`${slot.hour.toString().padStart(2, '0')}:${slot.minute.toString().padStart(2, '0')}`);
                          setCreateModalOpen(true);
                        }}
                        className={`${slot.minute === 0 ? 'border-b border-zinc-100' : 'border-b border-zinc-50'} cursor-pointer hover:bg-zinc-50`}
                        style={{ height: `${DAY_SLOT_HEIGHT_PX / (60 / RESIZE_GRID_MINUTES)}px` }}
                      >
                        {/* Empty - events are positioned absolutely */}
                      </DroppableSlot>
                    );
                  })}
                  
                  {/* Absolutely positioned events (grid events only - clamped to business hours) */}
                  {(() => {
                    const dayEvents = getEventsForDay(currentDate);
                    const { gridEvents } = categorizeEventsByBusinessHours(
                      dayEvents, 
                      businessHours.start, 
                      businessHours.end, 
                      businessHours?.timezone
                    );
                    const overlapLayoutMap = calculateOverlapLayout(gridEvents);
                    
                    return gridEvents.map((event) => {
                      const layout = overlapLayoutMap.get(event.id);
                      // Use clamped start/end if available for rendering
                      const renderEvent = {
                        ...event,
                        start: event.clampedStart || event.start,
                        end: event.clampedEnd || event.end,
                      };
                      return (
                        <ResizableEvent 
                          key={`${event.id}-${event.start}-${event.end}`} 
                          event={renderEvent} 
                          viewMode="day"
                          businessHours={businessHours}
                          onResize={handleResize}
                          onClick={(e) => handleEventClick(event, e)}
                          canResize={!isMobileDevice && event.status === "scheduled"}
                          renderWithHeight={true}
                          overlapLayout={layout}
                        >
                          {({ displayStart, displayEnd, isResizing }) => (
                            <DraggableEvent event={event} viewMode="day" disabled={isMobileDevice || event.status !== "scheduled"}>
                              <div
                                className={`h-full p-2 rounded-sm text-white cursor-pointer hover:opacity-90 overflow-hidden ${getStatusColor(event.status)} ${
                                  event.status !== "scheduled" ? "opacity-75" : ""
                                } ${isResizing ? "ring-2 ring-[#FF6600]" : ""}`}
                                data-testid={`day-event-${event.id}`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2 min-w-0">
                                    {event.status === "scheduled" && !isMobileDevice && (
                                      <DotsSixVertical size={16} className="opacity-60 flex-shrink-0" />
                                    )}
                                    <div className="font-medium truncate">{event.title}</div>
                                  </div>
                                  <Badge variant="secondary" className="bg-white/20 text-white capitalize text-xs flex-shrink-0 ml-1">
                                    {event.status}
                                  </Badge>
                                </div>
                                <div className="text-sm opacity-80 mt-1 truncate">
                                  <User size={12} className="inline mr-1" />
                                  {event.customer}
                                </div>
                                <div className="text-xs opacity-70 mt-1">
                                  <Clock size={12} className="inline mr-1" />
                                  {displayStart ? format(displayStart, "h:mm a") : ""}
                                  {displayEnd && ` - ${format(displayEnd, "h:mm a")}`}
                                </div>
                              </div>
                            </DraggableEvent>
                          )}
                        </ResizableEvent>
                      );
                    });
                  })()}
                </div>
              </div>

              {/* After Close Strip for Day View */}
              {(() => {
                const dayKey = format(currentDate, "yyyy-MM-dd");
                const dayEvents = getEventsForDay(currentDate);
                const { afterCloseItems } = categorizeEventsByBusinessHours(
                  dayEvents, 
                  businessHours.start, 
                  businessHours.end, 
                  businessHours?.timezone
                );
                
                if (afterCloseItems.length === 0) return null;
                
                return (
                  <div className="px-4 py-2 border-t border-zinc-100 bg-zinc-50/50">
                    <OffHoursStrip
                      items={afterCloseItems}
                      label="After close"
                      type="after"
                      dayKey={dayKey}
                      onItemClick={(item) => {
                        setSelectedEvent(item);
                        setDetailModalOpen(true);
                      }}
                    />
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        )}

        {/* Legend */}
        <div className="flex flex-wrap gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-blue-500 rounded-sm" />
            <span>Scheduled</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-amber-500 rounded-sm" />
            <span>In Progress</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-emerald-500 rounded-sm" />
            <span>Completed</span>
          </div>
          {!isMobileDevice && (
            <div className="flex items-center gap-2 ml-auto text-zinc-500">
              <DotsSixVertical size={16} />
              <span>Drag to move, resize edges to adjust time</span>
            </div>
          )}
        </div>

        {/* Create Visit Modal - Shared component */}
        <ScheduleVisitModal
          open={createModalOpen}
          onOpenChange={setCreateModalOpen}
          jobs={jobs}
          technicians={technicians}
          allTechnicians={allTechnicians}
          initialDate={selectedDate}
          initialTime={selectedTime}
          onVisitScheduled={handleVisitScheduled}
          businessHours={businessHours}
          onBeforeSubmit={(selectedTechIds, proceedCallback) => {
            // Check for Spanish-speaking techs without entitlement
            const spanishIds = findSpanishTechIds(selectedTechIds);
            
            if (spanishIds.length > 0) {
              // Spanish techs selected - show CTA
              setSpanishTechIds(spanishIds);
              // Store the proceed callback to execute on dismiss
              setPendingSaveCallback(() => proceedCallback);
              setSpanishCTAOpen(true);
              return false; // Don't proceed yet
            }
            return true; // No Spanish techs - proceed immediately
          }}
        />

        {/* Event Detail Modal */}
        <Dialog open={detailModalOpen} onOpenChange={(open) => {
          if (!open) {
            setIsEditingVisit(false);
            setShowDeleteVisitConfirm(false);
            setShowOutsideHoursWarning(false);
            setSpanishTechIds([]); // Reset Spanish tech IDs
          }
          setDetailModalOpen(open);
        }}>
          <DialogContent className="sm:max-w-md" data-testid="visit-detail-modal">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl uppercase">
                {isEditingVisit ? "Edit Visit" : "Visit Details"}
              </DialogTitle>
            </DialogHeader>
            
            <DialogBody>
              {selectedEvent && (
                <div className="space-y-4">
                {/* Delete Confirmation */}
                {showDeleteVisitConfirm && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm font-medium text-red-800 mb-3">Delete this visit?</p>
                    <p className="text-xs text-red-600 mb-3">This action cannot be undone. Notes and photos will also be deleted.</p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowDeleteVisitConfirm(false)}
                        className="border-red-200"
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={deleteVisit}
                        className="bg-red-600 hover:bg-red-700 text-white"
                        data-testid="cal-confirm-delete-visit"
                      >
                        Delete Visit
                      </Button>
                    </div>
                  </div>
                )}

                {/* Outside Hours Warning */}
                {showOutsideHoursWarning && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-md">
                    <p className="text-sm font-medium text-amber-800 mb-3">
                      This visit is scheduled outside business hours ({businessHours.start} - {businessHours.end})
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowOutsideHoursWarning(false)}
                        className="border-amber-200"
                      >
                        Go Back
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => saveVisitEdit(true)}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                      >
                        Schedule Anyway
                      </Button>
                    </div>
                  </div>
                )}

                {/* Edit Form */}
                {isEditingVisit && !showDeleteVisitConfirm && !showOutsideHoursWarning && (
                  <div className="space-y-4">
                    {/* Spanish Translation CTA - inline panel (appears when Save is clicked with Spanish techs) */}
                    {spanishCTAOpen && (
                      <SpanishTranslationCTA
                        onContinue={handleCTAContinue}
                        onUpgrade={handleCTAUpgrade}
                        onRemoveSpanishTechs={handleCTARemoveSpanishTechs}
                      />
                    )}
                    
                    {/* Inline Watchdog Warning Panel - appears when save is taking too long */}
                    {visitWatchdogFired && isSavingVisit && (
                      <div 
                        className="p-4 bg-amber-50 border border-amber-200 rounded-md"
                        data-testid="visit-watchdog-panel"
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-5 h-5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                          <span className="text-sm font-medium text-amber-800">Still working…</span>
                        </div>
                        <p className="text-sm text-amber-700 mb-3">
                          You can keep waiting, or cancel and retry.
                        </p>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleVisitCancelRetry}
                          className="w-full h-10 border-2 border-amber-400 text-amber-800 hover:bg-amber-100"
                          data-testid="visit-cancel-retry-btn"
                        >
                          Cancel & Retry
                        </Button>
                      </div>
                    )}
                    
                    {/* Edit form fields - only show when CTA is not visible */}
                    {!spanishCTAOpen && (
                    <>
                    {/* Job & Customer - Not Editable */}
                    <div className="p-3 bg-zinc-100 border border-zinc-200 rounded-md">
                      <div className="text-sm text-zinc-600">
                        <span className="font-medium">Job:</span> {selectedEvent.title}
                      </div>
                      <div className="text-sm text-zinc-500">
                        <span className="font-medium">Customer:</span> {selectedEvent.customer}
                      </div>
                    </div>

                    {/* Date */}
                    <div>
                      <Label>Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full mt-1 h-11 border-2 justify-start text-left font-normal">
                            <CalendarBlank size={16} className="mr-2" />
                            {format(editVisitForm.date, "PPP")}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <CalendarComponent 
                            mode="single" 
                            selected={editVisitForm.date} 
                            onSelect={(date) => date && setEditVisitForm(prev => ({ ...prev, date }))} 
                            initialFocus 
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Time */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Start Time</Label>
                        <Input
                          type="time"
                          value={editVisitForm.startTime}
                          onChange={(e) => setEditVisitForm(prev => ({ ...prev, startTime: e.target.value }))}
                          className="mt-1 h-11 border-2"
                        />
                      </div>
                      <div>
                        <Label>End Time</Label>
                        <Input
                          type="time"
                          value={editVisitForm.endTime}
                          onChange={(e) => setEditVisitForm(prev => ({ ...prev, endTime: e.target.value }))}
                          className="mt-1 h-11 border-2"
                        />
                      </div>
                    </div>

                    {/* Technicians */}
                    <div>
                      <Label>Assigned Technicians</Label>
                      <div className="mt-2 space-y-1 max-h-32 overflow-y-auto border border-zinc-200 rounded-md p-2 bg-white">
                        {technicians.map((tech) => (
                          <label key={tech.technician_id} className="flex items-center gap-2 p-1.5 rounded hover:bg-zinc-50 cursor-pointer">
                            <Checkbox
                              checked={editVisitForm.assigned_technicians.includes(tech.technician_id)}
                              onCheckedChange={(checked) => {
                                setEditVisitForm(prev => ({
                                  ...prev,
                                  assigned_technicians: checked
                                    ? [...prev.assigned_technicians, tech.technician_id]
                                    : prev.assigned_technicians.filter(id => id !== tech.technician_id)
                                }));
                              }}
                            />
                            <span className="text-sm">{tech.name}</span>
                            {tech.is_owner && <Badge variant="secondary" className="text-xs">Owner</Badge>}
                            {tech.preferred_language === "es" && <Badge variant="secondary" className="text-xs">ES</Badge>}
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Visit Address */}
                    {visitAddresses.length > 0 && (
                      <div>
                        <Label>Visit Address</Label>
                        <Select
                          value={editVisitForm.customer_address_id || ""}
                          onValueChange={(v) => setEditVisitForm(prev => ({ ...prev, customer_address_id: v }))}
                        >
                          <SelectTrigger className="mt-1 h-11 border-2">
                            <SelectValue placeholder="Select address" />
                          </SelectTrigger>
                          <SelectContent>
                            {visitAddresses.map((addr) => (
                              <SelectItem key={addr.id} value={addr.id}>
                                {addr.label}: {addr.street}, {addr.city}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    </>
                    )}

                    </div>
                  )}

                {/* Normal View */}
                {!isEditingVisit && !showDeleteVisitConfirm && !showOutsideHoursWarning && (
                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-md flex items-center justify-center ${getStatusColor(selectedEvent.status)}`}>
                        <CalendarBlank size={24} className="text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-lg truncate">{selectedEvent.title}</h3>
                        <p className="text-sm text-zinc-500 flex items-center gap-1">
                          <User size={14} />
                          <span className="truncate">{selectedEvent.customer}</span>
                        </p>
                      </div>
                      <Badge variant="outline" className="capitalize shrink-0">{selectedEvent.status}</Badge>
                    </div>

                    <div className="bg-zinc-50 p-4 rounded-md space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <CalendarBlank size={16} className="text-zinc-400 shrink-0" />
                        <span>{selectedEvent.start ? format(parseISOInCompanyTimezone(selectedEvent.start, businessHours?.timezone), "EEEE, MMMM d, yyyy") : "No date"}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Clock size={16} className="text-zinc-400 shrink-0" />
                        <span>
                          {selectedEvent.start ? format(parseISOInCompanyTimezone(selectedEvent.start, businessHours?.timezone), "h:mm a") : ""}
                          {selectedEvent.end && ` - ${format(parseISOInCompanyTimezone(selectedEvent.end, businessHours?.timezone), "h:mm a")}`}
                        </span>
                      </div>
                      {selectedEvent.technicians?.length > 0 && (
                        <div className="flex items-center gap-2 text-sm text-zinc-500">
                          <User size={16} className="text-zinc-400 shrink-0" />
                          <span>{selectedEvent.technicians.length} technician(s) assigned</span>
                        </div>
                      )}
                      {selectedEvent.visit_group_id && (
                        <div className="text-xs text-zinc-400 pl-6">
                          Part of multi-day visit series
                        </div>
                      )}
                    </div>
                  </div>
                )}
                </div>
              )}
            </DialogBody>

            {/* Footer with action buttons */}
            {selectedEvent && !showDeleteVisitConfirm && !showOutsideHoursWarning && !spanishCTAOpen && (
              <DialogFooter>
                {isEditingVisit ? (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => setIsEditingVisit(false)}
                      className="flex-1 sm:flex-none border-2"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => saveVisitEdit()}
                      disabled={isSavingVisit}
                      className="flex-1 sm:flex-none bg-[#FF6600] hover:bg-[#CC5200] text-white disabled:opacity-50"
                      data-testid="save-visit-edit"
                    >
                      {isSavingVisit ? "Saving..." : "Save Changes"}
                    </Button>
                  </>
                ) : (
                  <>
                    {/* Action buttons based on status */}
                    {selectedEvent.status === "scheduled" && (
                      <>
                        <div className="flex gap-2 sm:hidden w-full">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={openVisitEdit}
                            className="h-10 w-10 p-0"
                            data-testid="edit-visit-btn"
                            title="Edit visit"
                          >
                            <PencilSimple size={18} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowDeleteVisitConfirm(true)}
                            className="h-10 w-10 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            data-testid="cal-delete-visit-btn"
                            title="Delete visit"
                          >
                            <Trash size={18} />
                          </Button>
                        </div>
                        <Button 
                          onClick={() => startVisit(selectedEvent.id)} 
                          className="flex-1 sm:flex-none bg-amber-500 hover:bg-amber-600 text-white"
                        >
                          <Play size={18} className="mr-2" />
                          Start Visit
                        </Button>
                      </>
                    )}
                    {selectedEvent.status === "in_progress" && (
                      <Button 
                        onClick={() => completeVisit(selectedEvent.id)} 
                        className="flex-1 sm:flex-none bg-emerald-500 hover:bg-emerald-600 text-white"
                      >
                        <CheckCircle size={18} className="mr-2" />
                        Complete
                      </Button>
                    )}
                    <Link to={`/jobs/${selectedEvent.job_id || ''}`} className="flex-1 sm:flex-none">
                      <Button variant="outline" className="w-full border-2">
                        <Briefcase size={18} className="mr-2" />
                        View Job
                      </Button>
                    </Link>
                    {/* Desktop action icons */}
                    {selectedEvent.status === "scheduled" && (
                      <div className="hidden sm:flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={openVisitEdit}
                          className="h-10 w-10 p-0"
                          data-testid="edit-visit-btn"
                          title="Edit visit"
                        >
                          <PencilSimple size={18} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowDeleteVisitConfirm(true)}
                          className="h-10 w-10 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                          data-testid="cal-delete-visit-btn-desktop"
                          title="Delete visit"
                        >
                          <Trash size={18} />
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>

        {/* Drag Overlay - Shows what's being dragged */}
        <DragOverlay>
          {draggedEvent && (
            <div className={`text-xs p-2 rounded-sm text-white shadow-lg ${getStatusColor(draggedEvent.status)}`}>
              <div className="font-medium">{draggedEvent.title}</div>
              <div className="opacity-80">{draggedEvent.customer}</div>
            </div>
          )}
        </DragOverlay>
      </div>

      {/* Quick Actions */}
      <BottomQuickActions />
    </DndContext>
  );
}
